import { EnhancedPDFGenerator } from "./enhancedPdfGenerator";
import { extractElementaryActions } from "./scenarioDataNormalizer";
import { normalizeRuleStatus, getRuleStatusLabelFr, getRuleStatusLabelEn } from "./ruleStatusNormalizer";
import { getGravityByLevel } from "../constants/gravityLevels";
import { buildCoverageRegistry } from "../components/Atelier5/ActiviteA/coverageMatrix";
import { STRINGS } from "./matrixReportStrings";

function getScenarioVraisemblance(scenario) {
  if (scenario.scenarioVraisemblance) return scenario.scenarioVraisemblance;
  if (scenario.vraisemblance) return scenario.vraisemblance;
  const ops = scenario.operationalScenarios || [];
  if (ops.length > 0) {
    return Math.max(...ops.map(op =>
      Number(op.scenarioVraisemblance || op.vraisemblance || 2)
    ));
  }
  return 2;
}

function normalizeAttackPathId(attackPathId) {
  if (!attackPathId || typeof attackPathId !== 'string') return '';
  const parts = attackPathId.split('-');
  if (parts.length > 0) {
    const lastPart = parts[parts.length - 1];
    if (/^\d{13,}$/.test(lastPart)) {
      if (parts.length >= 5) {
        const secondToLast = parts[parts.length - 2];
        if (secondToLast === 'direct' || /^\d{13,}$/.test(secondToLast)) {
          return parts.slice(0, -1).join('-');
        }
      }
    }
  }
  return attackPathId;
}

/**
 * Matrix Report Generator - Specialized report fulfilling "Plan de Traitement des Risques" requirements.
 * Focuses on:
 * 1. Full data visibility (no truncation).
 * 2. Specific "Atelier 1 Maps" ("Mesures de Contrôle") layout.
 * 3. Including specific context sections and detailed risk treatment tables.
 * 4. Workshop 2 Integration (Sources & Associations) with correct hierarchy and formatting.
 * 5. Workshop 3 Integration (Ecosystem & Strategic Scenarios).
 */
class MatrixReportGenerator {
  constructor(reportData, analysisId, lang = 'fr') {
    this.reportData = reportData;
    this.analysisId =
      typeof analysisId === "object" && analysisId !== null
        ? analysisId.analysisId
        : analysisId || reportData?.analysisId;
    this.language = lang === 'en' ? 'en' : 'fr';
    this.T = STRINGS[this.language] || STRINGS.fr;
    this.getRuleStatusLabel = this.language === 'en' ? getRuleStatusLabelEn : getRuleStatusLabelFr;

    // Initialize the base generator
    this.pdf = new EnhancedPDFGenerator({
      title: this.T.pdfTitle,
      subject: this.T.pdfSubject,
      author: "EBIOS RM System",
      language: this.language,
      keywords: this.T.pdfKeywords,
    });
  }

  /**
   * Calculate threat level (niveau) based on motivation, activity, and resources
   * Same formula used in Activite2.js
   */
  calculateNiveau(motivation, activite, ressources) {
    const normalizedMotivation = String(motivation || "").toLowerCase();
    const normalizedActivite = String(activite || "").toLowerCase();
    const normalizedRessources = String(ressources || "").toLowerCase();

    const motivationScore = normalizedMotivation.startsWith("elev") || normalizedMotivation.startsWith("high")
      ? 3
      : normalizedMotivation.startsWith("moyen") || normalizedMotivation.startsWith("med")
        ? 2
        : 1;
    const activiteScore = normalizedActivite.startsWith("elev") || normalizedActivite.startsWith("high")
      ? 3
      : normalizedActivite.startsWith("moyen") || normalizedActivite.startsWith("med")
        ? 2
        : 1;
    const ressourcesScore = normalizedRessources.startsWith("import") || normalizedRessources.startsWith("high")
      ? 3
      : normalizedRessources.startsWith("moyen") || normalizedRessources.startsWith("med")
        ? 2
        : 1;

    const totalScore = motivationScore + activiteScore + ressourcesScore;

    if (totalScore < 6) return 1;
    if (totalScore === 6) return 2;
    return 3;
  }

  async generateMatrixReport() {
    try {
      console.log(`[MatrixReportGenerator] Starting report generation (${this.language})`);

      // 1. Cover Page
      this.generateMatrixCoverPage();

      // 2. Reserve TOC Pages
      this.pdf.reserveTableOfContentsPages();

      // 3. Start Content Section
      this.pdf.startContentSection();

      // Summary
      await this.generateMatrixSummary();
      this.pdf.addSpace(5);

      // 2. Chapter 1: CADRAGE ET SOCLE DE SECURITE
      await this.generateAtelier1Cadrage();

      // 3. Chapter 2: ATELIER 2 - SOURCES ET SCÉNARIOS
      this.pdf.addNewPage();
      this.pdf.addSectionHeading(
        this.T.w2.title,
        1,
      );
      this.pdf.addParagraph(
        this.T.w2.desc,
      );
      this.pdf.addSpace(3);

      // 2.1 Sources de Risque
      await this.generateAtelier2Sources();

      // 2.2 Associations SR/ER
      await this.generateAtelier2Associations();

      // 4. Chapter 3: ATELIER 3 - SCÉNARIOS STRATÉGIQUES
      this.pdf.addNewPage();
      this.pdf.addSectionHeading(
        this.T.w3.title,
        1,
      );
      this.pdf.addParagraph(
        this.T.w3.desc,
      );
      this.pdf.addSpace(3);

      // 3.1 Cartographie Écosystème
      await this.generateAtelier3Ecosystem();

      // 3.2 Scénarios Stratégiques
      await this.generateAtelier3Scenarios();

      // 3.3 Chemins d'Attaque (Atelier 3 - Activité 3)
      await this.generateAtelier3AttackPaths();

      // 3.4 Mesures de Sécurité
      await this.generateAtelier3SecurityMeasures();

      // 3.5 Plan de traitement de risque atelier 3
      await this.generateAtelier3EventProgression();

      // 4. ATELIER 4 : SCÉNARIOS OPÉRATIONNELS
      this.pdf.addNewPage();
      this.pdf.addSectionHeading(
        this.T.w4.title,
        1,
      );
      this.pdf.addParagraph(
        this.T.w4.desc,
      );
      this.pdf.addSpace(3);

      // 4.1 Intelligence des Menaces
      await this.generateAtelier4ThreatIntelligence();

      // 4.2 Scénarios Opérationnels
      await this.generateAtelier4OperationalScenariosDetailed();

      // 5. Chapter 5: ATELIER 5 - TRAITEMENT DU RISQUE ET SUIVI
      this.pdf.addNewPage();
      this.pdf.addSectionHeading(
        this.T.w5.title,
        1,
      );
      this.pdf.addParagraph(
        this.T.w5.desc,
      );
      this.pdf.addSpace(3);

      // 5. Vue Globale, Traitement et Suivi
      await this.generateWorkshop5Section();

      // ANNEXES
      this.pdf.addNewPage();
      await this.generateAnnexes();

      // Generate TOC at the end (fills reserved pages)
      this.pdf.generateTableOfContents();

      console.log("[MatrixReportGenerator] Report generation completed");
      return this.pdf.getBlob();
    } catch (error) {
      console.error("[MatrixReportGenerator] Error:", error);
      throw error;
    }
  }

  /**
   * Generate Custom Matrix Cover Page matching SecurityControlsPdfGenerator layout
   * Updated Title: "Plan de Traitement des Risques" / "Risk Treatment Plan"
   */
  generateMatrixCoverPage() {
    // Access raw jsPDF instance from EnhancedPDFGenerator wrapper
    const doc = this.pdf.pdf;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Metadata aliases
    const metadata = this.reportData.metadata || {};
    const title = metadata.analysisName || (this.language === 'en' ? "EBIOS RM Analysis" : "Analyse EBIOS RM");
    const company = metadata.companyName || (this.language === 'en' ? "Organization" : "Organisation");
    const version = metadata.version || "1.0";
    const ref =
      metadata.reference ||
      `REF-${String(this.analysisId || "").substring(0, 8) || "####"}`;

    // Clean white background
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageWidth, pageHeight, "F");

    // Header table - Programme info
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(15, 15, 180, 10, "S");

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text(this.T.cover.program, 17, 21);
    doc.setFont("helvetica", "normal");
    doc.text(this.T.cover.analysis, 45, 21);

    // Internal classification label
    doc.setFillColor(200, 200, 200);
    doc.rect(135, 15, 60, 10, "FD");
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text(this.T.cover.internal, 155, 21);
    doc.setTextColor(0, 0, 0);

    // Document type
    doc.setFillColor(255, 255, 255);
    doc.rect(15, 25, 180, 8, "S");
    doc.setFont("helvetica", "bold");
    doc.text(this.T.cover.docType, 17, 30);
    doc.setFont("helvetica", "normal");
    doc.text(this.T.cover.docTitle, 45, 30);

    // Version and Reference
    doc.rect(15, 33, 90, 8, "S");
    doc.setFont("helvetica", "bold");
    doc.text(this.T.cover.version, 17, 38);
    doc.setFont("helvetica", "normal");
    doc.text(version, 37, 38);

    doc.rect(105, 33, 90, 8, "S");
    doc.setFont("helvetica", "bold");
    doc.text(this.T.cover.reference, 107, 38);
    doc.setFont("helvetica", "normal");
    doc.text(ref, 120, 38);

    // Main title
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(37, 99, 235);
    const mainTitleText = this.language === 'en' ? "Risk Analysis Report:" : "Rapport d'Analyse de Risques:";
    doc.text(mainTitleText, 105, 65, { align: "center" });

    doc.setFontSize(18);
    const analysisName =
      this.reportData.metadata?.analysisName || company;
    const titleLines = doc.splitTextToSize(analysisName, 170);
    titleLines.forEach((line, i) => {
      doc.text(line, 105, 80 + i * 9, { align: "center" });
    });

    // EBIOS RM title
    doc.setFontSize(22);
    doc.setTextColor(37, 99, 235);
    doc.text("EBIOS RM", 105, 110, { align: "center" });

    // Subtitle section
    doc.setFontSize(16);
    doc.setTextColor(16, 185, 129); // Green equivalent style or Blue for Matrix
    const subtitleText = this.language === 'en' ? "Risk Synthesis" : "Synthèse des Risques";
    doc.text(subtitleText, 105, 135, { align: "center" });
    doc.setTextColor(0, 0, 0);

    // Information table
    const tableStartY = 150;
    const rowHeight = 8;

    const tableData = this.language === 'en' ? [
      ["Program", "Global Risk Analysis"],
      ["Organization", company],
      ["Classification", "Internal"],
      ["Revision frequency", "Annual / after major changes"],
      ["Status", "In progress"],
      ["Generation date", new Date().toLocaleDateString("en-GB")],
    ] : [
      ["Programme", "Analyse de Risques Globale"],
      ["Organisation", company],
      ["Classification", "Interne"],
      ["Fréquence de révision", "Annuelle / chaque changement majeur"],
      ["Statut", "En cours"],
      ["Date de génération", new Date().toLocaleDateString("fr-FR")],
    ];

    tableData.forEach((row, index) => {
      const y = tableStartY + index * rowHeight;

      // Label column (blue background)
      doc.setFillColor(41, 82, 163);
      doc.rect(15, y, 65, rowHeight, "FD");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text(row[0], 17, y + 5.5);

      // Value column (light gray background)
      doc.setFillColor(235, 235, 235);
      doc.rect(80, y, 115, rowHeight, "FD");
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      doc.text(row[1], 82, y + 5.5);
    });

    // Footer
    const footerY = pageHeight - 20;
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.setFont("helvetica", "normal");
    const footerDocText = this.language === 'en'
      ? "EBIOS RM Methodology - Risk Treatment Plan"
      : "Méthodologie EBIOS RM - Plan de Traitement des Risques";
    const footerConfText = this.language === 'en'
      ? "Confidential document - Internal use only"
      : "Document confidentiel - Usage interne uniquement";
    doc.text(
      footerDocText,
      105,
      footerY,
      { align: "center" },
    );
    doc.text(
      footerConfText,
      105,
      footerY + 8,
      { align: "center" },
    );
  }

  /**
   * Generate Matrix Summary
   */
  async generateMatrixSummary() {
    const sumT = this.T.summary;
    this.pdf.addSectionHeading(this.language === 'en' ? "EXECUTIVE SUMMARY" : "SYNTHÈSE", 1, { skipNumbering: true });
    this.pdf.addParagraph(
      this.language === 'en'
        ? "This section provides an overview of the study, including the subject, major constraints, and overall risk profile."
        : "Ce paragraphe présente une vue d'ensemble de l'étude, incluant le sujet, les contraintes majeures et le profil de risque global.",
    );
    this.pdf.addSpace(3);

    const metadata = this.reportData.metadata || {};
    const workshop1 = this.reportData.workshops?.workshop1 || {};
    const globalContext = this.reportData.globalContext || {};
    const workshop5Data = this.reportData.workshops?.workshop5 || {};
    const dreadedEvents = workshop1.dreadedEvents || [];

    // 0.1 Sujet de l'étude (Context + Description)
    this.pdf.addSectionHeading(sumT.studySubject, 2);
    const description =
      workshop1.description ||
      globalContext.description ||
      sumT.noDesc;
    this.pdf.addParagraph(
      sumT.studyOn(metadata.analysisName || (this.language === 'en' ? "EBIOS RM Analysis" : "Analyse EBIOS RM")),
    );
    this.pdf.addParagraph(description);
    this.pdf.addSpace(3);

    // 0.2 Hypothèses et Contraintes
    this.pdf.addSectionHeading(
      sumT.assumptionsConstraints,
      2,
    );

    const assumptions = globalContext.assumptions || [];
    const constraints = globalContext.constraints || [];

    if (assumptions.length > 0 || constraints.length > 0) {
      if (constraints.length > 0) {
        this.pdf.addParagraph(sumT.identifiedConstraints);
        constraints
          .slice(0, 3)
          .forEach((c) => this.pdf.addParagraph(`- ${c.description || c}`));
        if (constraints.length > 3)
          this.pdf.addParagraph(sumT.andOthers(constraints.length - 3));
      }
      if (assumptions.length > 0) {
        this.pdf.addSpace(1);
        this.pdf.addParagraph(sumT.workingAssumptions);
        assumptions
          .slice(0, 3)
          .forEach((a) => this.pdf.addParagraph(`- ${a.description || a}`));
        if (assumptions.length > 3)
          this.pdf.addParagraph(sumT.andOthers(assumptions.length - 3));
      }
    } else {
      this.pdf.addParagraph(
        sumT.noAssumptions,
      );
    }
    this.pdf.addSpace(3);

    // 0.3 Risques Inhérents / Initiaux
    this.pdf.addSectionHeading(sumT.initialRisks, 2);
    // Calculate distribution of initial risks (feared events Severity)
    const deSeverityDist = {
      catastrophic: 0,
      critical: 0,
      major: 0,
      moderate: 0,
      minor: 0,
    };
    dreadedEvents.forEach((de) => {
      if (deSeverityDist[de.severity] !== undefined)
        deSeverityDist[de.severity]++;
    });

    this.pdf.addParagraph(
      sumT.deSeverityDist,
    );
    const deStats = [
      sumT.severityHeaders,
      [
        sumT.catCritical,
        String(deSeverityDist["catastrophic"] + deSeverityDist["critical"]),
      ],
      [sumT.major, String(deSeverityDist["major"])],
      [
        sumT.modMinor,
        String(deSeverityDist["moderate"] + deSeverityDist["minor"]),
      ],
    ];
    this.pdf.addTable(deStats[0], deStats.slice(1), {
      columnWidths: [100, 30],
      fontSize: 9,
    });
    this.pdf.addSpace(3);

    // 0.4 Plan de Traitement (High level stats)
    this.pdf.addSectionHeading(
      sumT.treatmentPlan,
      2,
    );
    const w5Scenarios = workshop5Data.strategicScenarios || [];

    this.pdf.addParagraph(
      sumT.treatmentAddressesScenarios(w5Scenarios.length),
    );
    this.pdf.addParagraph(
      sumT.measuresInclude,
    );
    this.pdf.addSpace(3);

    // 0.5 Risques Résiduels
    this.pdf.addSectionHeading(sumT.residualRisks, 2);
    const criticalResiduals = w5Scenarios.filter((s) => {
      const finalScore =
        s.temporalEvolution?.timelineData?.mediumTerm?.riskScore || 0;
      return finalScore >= 12; // Example threshold for high risk
    }).length;

    if (criticalResiduals > 0) {
      this.pdf.addParagraph(
        sumT.residualWarning(criticalResiduals),
      );
    } else {
      this.pdf.addParagraph(
        sumT.residualAcceptable,
      );
    }

    this.pdf.addSpace(5);
  }

  /**
   * 1. ATELIER 1 : CADRAGE ET SOCLE DE SECURITE
   */
  async generateAtelier1Cadrage() {
    const w1T = this.T.w1;
    this.pdf.addSectionHeading(w1T.title, 1);

    // 1.1. Informations relatives à l'étude (Cadre de l'étude)
    this.pdf.addSectionHeading(
      w1T.infoTitle,
      2,
    );

    const metadata = this.reportData.metadata || {};
    const infoTableData = [
      [w1T.version, metadata.version || "1.0"],
      [w1T.date, this.language === 'en' ? new Date().toLocaleDateString("en-GB") : new Date().toLocaleDateString("fr-FR")],
      [w1T.status, w1T.inProgress],
      [w1T.classification, w1T.internal],
    ];
    // Use autoTable for info
    this.pdf.addTable(w1T.infoHeaders, infoTableData, {
      columnWidths: [60, 100],
      fontSize: 10,
    });
    this.pdf.addSpace(5);

    // 1.2. Périmètre métier et technique
    this.pdf.addSectionHeading(
      w1T.scopeTitle,
      2,
    );

    // Introduction logic for Presentation (System)
    const workshop1 = this.reportData.workshops?.workshop1 || {};
    const globalContext = this.reportData.globalContext || {};

    // Scope / System Presentation (Full text)
    const description =
      workshop1.description ||
      globalContext.description ||
      (this.language === 'en' ? "No description provided." : "Aucune description fournie.");
    this.pdf.addSectionHeading(w1T.systemPresentation, 3);
    this.pdf.addParagraph(description);
    this.pdf.addSpace(3);

    // Missions (Full text)
    const missions = workshop1.missions || [];
    if (missions.length > 0) {
      this.pdf.addSectionHeading(w1T.missions, 3);
      missions.forEach((m) => {
        this.pdf.addParagraph(`• **${m.name}**: ${m.description || ""}`);
      });
      this.pdf.addSpace(3);
    }

    // Business Values (Valeurs Métier)
    const businessValues = workshop1.businessValues || [];
    if (businessValues.length > 0) {
      this.pdf.addSectionHeading(w1T.businessValues, 3);
      this.pdf.addParagraph(w1T.bvDescription);
      this.pdf.addSpace(2);
      const bvRows = businessValues.map((bv) => [
        bv.name,
        (bv.nature === 'processus' ? w1T.natureProcess : bv.nature === 'information' ? w1T.natureInfo : bv.nature || "-"),
        (bv.proprietaire ? (bv.proprietaire.nom || "-") + (bv.proprietaire.type ? ' (' + bv.proprietaire.type + ')' : '') : "-"),
        bv.description || "-",
      ]);
      this.pdf.addTable(
        w1T.bvHeaders,
        bvRows,
      );
      this.pdf.addSpace(3);
    }

    // Supporting Assets (Biens Supports)
    const allSupportAssets = (workshop1.businessValues || []).flatMap((bv) =>
      (bv.supportAssets || []).map((asset) => ({
        ...asset,
        businessValueName: bv.name || "-",
      })),
    );
    if (allSupportAssets.length > 0) {
      this.pdf.addSectionHeading(w1T.supportingAssets, 3);
      this.pdf.addParagraph(
        w1T.saDescription(allSupportAssets.length)
      );
      this.pdf.addSpace(3);

      const assetTypeLabels = this.T.lookups.assetTypes;

      const saHeaders = w1T.saHeaders;
      const saRows = allSupportAssets.map((sa) => {
        let vendorProduct = "-";
        if (sa.vendor && sa.product) {
          vendorProduct = `${sa.vendor} / ${sa.product}`;
        } else if (sa.vendor) {
          vendorProduct = sa.vendor;
        } else if (sa.product) {
          vendorProduct = sa.product;
        }
        const version = sa.version ? `v${sa.version}` : "-";
        return [
          sa.shortId || "N/A",
          sa.name || (this.language === 'en' ? "Unnamed" : "Sans nom"),
          assetTypeLabels[sa.type] || sa.type || "-",
          sa.owner || "-",
          sa.location || "-",
          vendorProduct,
          version,
        ];
      });
      this.pdf.addTable(saHeaders, saRows, {
        columnWidths: [15, 30, 22, 22, 22, "auto", 15],
        headStyles: {
          fillColor: [34, 197, 94],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
        styles: {
          lineColor: [226, 232, 240],
          lineWidth: 0.5,
        },
        columnStyles: {
          0: { halign: "center" },
          2: { halign: "center" },
          6: { halign: "center" },
        },
      });
      this.pdf.addSpace(5);

      // Asset type distribution summary
      const assetTypeStats = {};
      allSupportAssets.forEach((asset) => {
        const typeKey = asset.type || "unknown";
        const typeLabel = assetTypeLabels[typeKey] || typeKey;
        assetTypeStats[typeLabel] = (assetTypeStats[typeLabel] || 0) + 1;
      });
      this.pdf.addParagraph(w1T.saDistTitle);
      Object.entries(assetTypeStats).forEach(([type, count]) => {
        const percentage = Math.round((count / allSupportAssets.length) * 100);
        this.pdf.addParagraph(`  • ${type} : ${count} (${percentage}%)`);
      });
      this.pdf.addSpace(3);
    }

    // Informations Générales (Assumption, Constraint, Stakeholders)
    const assumptions = globalContext.assumptions || [];
    const constraints = globalContext.constraints || [];
    const stakeholders = globalContext.stakeholders || [];

    if (
      assumptions.length > 0 ||
      constraints.length > 0 ||
      stakeholders.length > 0
    ) {
      this.pdf.addSectionHeading(w1T.generalInfo, 3);

      if (assumptions.length > 0) {
        this.pdf.addSectionHeading(w1T.assumptions, 4);
        assumptions.forEach((a) =>
          this.pdf.addParagraph(`- ${a.description || a}`),
        );
      }
      if (constraints.length > 0) {
        this.pdf.addSectionHeading(w1T.constraints, 4);
        constraints.forEach((c) =>
          this.pdf.addParagraph(`- ${c.description || c}`),
        );
      }
      if (stakeholders.length > 0) {
        this.pdf.addSectionHeading(w1T.raciMatrix, 4);
        const workshopIds = stakeholders.length > 0 && stakeholders[0].workshopRoles
          ? Object.keys(stakeholders[0].workshopRoles).sort((a, b) => {
              const numA = parseInt(a.match(/\d+/)?.[0] || '0');
              const numB = parseInt(b.match(/\d+/)?.[0] || '0');
              return numA - numB;
            })
          : ['Atelier 1', 'Atelier 2', 'Atelier 3', 'Atelier 4', 'Atelier 5'];

        const prefix = this.language === 'en' ? 'W' : 'A';
        const shortLabels = workshopIds.map(id => `${prefix}${id.match(/\d+/)?.[0] || ''}`);
        const shRows = stakeholders.map((s) => {
          const wr = s.workshopRoles || {};
          const position = s.position || s.role || "-";
          return [
            s.name,
            position,
            ...workshopIds.map(wid => {
              const roles = Array.isArray(wr[wid]) ? wr[wid] : [];
              return roles.length > 0 ? roles.join(', ') : '-';
            }),
            s.contact || "-",
          ];
        });
        const headerRole = this.language === 'en' ? "Role" : "Rôle";
        const headerName = this.language === 'en' ? "Name" : "Nom";
        const headerContact = "Contact";
        this.pdf.addTable([headerName, headerRole, ...shortLabels, headerContact], shRows);
      }

      this.pdf.addSpace(3);
      // Responsables des Mesures de Contrôle
      const responsiblePersonsList = this.reportData.workshops?.workshop1?.context?.responsiblePersonsList || [];
      if (responsiblePersonsList.length > 0) {
        this.pdf.addSectionHeading(w1T.respControls, 4);
        const rHeaders = w1T.respHeaders;
        const rRows = responsiblePersonsList.map(r => [r.name || '', r.role || '-']);
        this.pdf.addTable(rHeaders, rRows);
      }

      this.pdf.addSpace(3);
    }

    // 1.3 Identification des événements redoutés
    this.pdf.addSectionHeading(
      w1T.dreadedEventsSection,
      2,
    );

    const dreadedEvents = workshop1.dreadedEvents || [];
    const systemBvId = '__global__';
    const erEvents = dreadedEvents.filter(de => de.businessValue !== systemBvId);
    const rnEvents = dreadedEvents.filter(de => de.businessValue === systemBvId);

    const pillarLabels = this.T.lookups.pillars;

    const deSeverityLabels = this.language === 'en' ? {
      minor: 'Minor',
      moderate: 'Moderate',
      major: 'Major',
      critical: 'Critical',
      catastrophic: 'Catastrophic',
    } : {
      minor: 'Mineure',
      moderate: 'Modérée',
      major: 'Majeure',
      critical: 'Critique',
      catastrophic: 'Catastrophique',
    };

    const buildDesc = (de) => {
      let descText = de.description || '';
      if (de.impacts && de.impacts.length > 0) {
        const normalizedImpacts = de.impacts.map(i => typeof i === 'string' ? i : i.impact || String(i));
        descText += '\nImpacts:\n- ' + normalizedImpacts.join('\n- ');
      }
      return descText || '-';
    };

    // 1.3.1 Événements Redoutés
    if (erEvents.length > 0) {
      this.pdf.addSectionHeading(w1T.dreadedEventsTitle, 3);
      const erHeaders = w1T.erHeaders;
      const erRows = erEvents.map((de) => {
        const bvId = de.businessValue || de.businessValueId;
        const bv = businessValues.find((b) => b.id === bvId);
        return [
          bv ? bv.name : bvId || '-',
          pillarLabels[de.securityPillar] || de.securityPillar || '-',
          de.name || (this.language === 'en' ? 'Unnamed' : 'Sans nom'),
          buildDesc(de),
          deSeverityLabels[de.severity] || this.formatSeverity(de.severity),
        ];
      });
      this.pdf.addTable(erHeaders, erRows, {
        fontSize: 9,
        columnWidths: [32, 23, 43, 58, 19],
      });
      this.pdf.addSpace(3);
    }

    // 1.3.2 Risques Non Intentionnels
    if (rnEvents.length > 0) {
      this.pdf.addSectionHeading(w1T.nonIntRisksTitle, 3);
      const rnHeaders = w1T.rnHeaders;
      const rnRows = rnEvents.map((de) => [
        pillarLabels[de.securityPillar] || de.securityPillar || '-',
        de.name || (this.language === 'en' ? 'Unnamed' : 'Sans nom'),
        buildDesc(de),
        deSeverityLabels[de.severity] || this.formatSeverity(de.severity),
      ]);
      this.pdf.addTable(rnHeaders, rnRows, {
        fontSize: 9,
        columnWidths: [25, 45, 70, 20],
      });
      this.pdf.addSpace(3);
    }

    if (dreadedEvents.length === 0) {
      this.pdf.addParagraph(
        w1T.noDreadedEvents,
      );
    }
    this.pdf.addSpace(5);

    // 1.4. Relation entre les besoins de sécurité des valeurs métier et la gravité des événements redoutés
    this.pdf.addSectionHeading(
      w1T.bvDeRelTitle,
      2,
    );

    if (businessValues.length > 0) {
      this.pdf.addParagraph(
        w1T.bvDeRelDesc,
      );
      this.pdf.addSpace(3);

      const relHeaders = w1T.relHeaders;

      // Generate detailed rows with rowSpan support
      const relRows = [];

      businessValues.forEach((bv) => {
        const pillars = [
          { key: "disponibilite", label: pillarLabels.disponibilite || "Disponibilité" },
          { key: "integrite", label: pillarLabels.integrite || "Intégrité" },
          { key: "confidentialite", label: pillarLabels.confidentialite || "Confidentialité" },
          { key: "preuve", label: pillarLabels.preuve || "Preuve" },
          { key: "tracabilite", label: pillarLabels.tracabilite || "Traçabilité" },
        ];

        // 1. Calculate row spans for BV columns
        const activePillarsData = [];
        let totalRowsForBV = 0;

        pillars.forEach((pillar) => {
          const level = bv[pillar.key];
          // Active if level is defined/non-zero OR if securityPillars list contains it
          const isActive =
            (level && level !== "0") ||
            (bv.securityPillars && bv.securityPillars.includes(pillar.key));

          if (isActive) {
            const matchingDEs = dreadedEvents.filter((de) => {
              const matchBV =
                de.businessValueId === bv.id || de.businessValue === bv.id;
              const matchPillar = de.securityPillar === pillar.key;
              return matchBV && matchPillar;
            });

            if (matchingDEs.length > 0) {
              const rowCount = matchingDEs.length;
              totalRowsForBV += rowCount;

              activePillarsData.push({
                pillar: pillar,
                dreadedEvents: matchingDEs,
                rowCount: rowCount,
              });
            }
          }
        });

        if (totalRowsForBV === 0) totalRowsForBV = 1;

        // 2. Generate actual rows
        let isFirstRowForBV = true;

        if (activePillarsData.length > 0) {
          activePillarsData.forEach((data) => {
            const { pillar, dreadedEvents, rowCount } = data;
            let isFirstRowForPillar = true;

            const needLabel = pillar.label;

            if (dreadedEvents.length > 0) {
              dreadedEvents.forEach((de) => {
                const row = [];

                // BV Name (Merged)
                if (isFirstRowForBV) {
                  row.push({
                    content: bv.name || "-",
                    rowSpan: totalRowsForBV,
                    styles: { valign: "middle" },
                  });
                }

                // BV Abbrev (Merged)
                if (isFirstRowForBV) {
                  row.push({
                    content: bv.shortId || "-",
                    rowSpan: totalRowsForBV,
                    styles: { valign: "middle" },
                  });
                }

                // Need Label (Merged for this pillar)
                if (isFirstRowForPillar) {
                  row.push({
                    content: needLabel,
                    rowSpan: rowCount,
                    styles: { valign: "middle" },
                  });
                }

                // DE Details
                row.push(de.name || "-");
                row.push(de.shortId || (this.language === 'en' ? "DE" : "ER"));
                row.push(this.formatSeverity(de.severity));

                relRows.push(row);

                isFirstRowForBV = false;
                isFirstRowForPillar = false;
              });
            }
          });
        } else {
          // No active pillars - placeholder row
          relRows.push([
            {
              content: bv.name || "-",
              rowSpan: 1,
              styles: { valign: "middle" },
            },
            {
              content: bv.shortId || "-",
              rowSpan: 1,
              styles: { valign: "middle" },
            },
            "-",
            "-",
            "-",
            "-",
          ]);
        }
      });

      this.pdf.addTable(relHeaders, relRows, {
        fontSize: 8,
        columnWidths: [35, 15, 30, 60, 15, 20],
      });
    } else {
      this.pdf.addParagraph(
        w1T.relInsufficient,
      );
    }
    this.pdf.addSpace(5);

    // 1.5. Socle de sécurité (Tableau de traitement des risques)
    this.pdf.addSectionHeading(w1T.baselineTitle, 2);

    const severityLabels = this.language === 'en' ? {
      minor: "Minor (G1)",
      moderate: "Moderate (G2)",
      major: "Major (G3)",
      critical: "Critical (G4)",
      catastrophic: "Catastrophic (G5)",
    } : {
      minor: "Mineure (G1)",
      moderate: "Modérée (G2)",
      major: "Majeure (G3)",
      critical: "Critique (G4)",
      catastrophic: "Catastrophique (G5)",
    };

    const decisionLabels = this.language === 'en' ? {
      implemented: "Done",
      partial: "Partial",
      planned: "Planned",
      apply_before: "Apply before",
      abandoned: "Abandoned",
    } : {
      implemented: "Mise en œuvre",
      partial: "Partielle",
      planned: "Planifiée",
      apply_before: "À appliquer avant",
      abandoned: "Abandonnée",
    };

    // Resolve security framework rules for rule name lookup
    const w1 = this.reportData.workshops?.workshop1 || {};
    const securityFrameworkData =
      this.reportData.securityFramework ||
      w1.securityFramework ||
      w1.securityBaseline ||
      {};
    const allRules = Object.values(
      securityFrameworkData.selectedRules || {},
    ).flat();

    // Data source: analysisControls (from Atelier 5 or global control implementation)
    const analysisControlsData = this.reportData.analysisControls || {};
    const treatmentPlans =
      analysisControlsData.plan || analysisControlsData || {};

    let planEntries = [];
    if (treatmentPlans && typeof treatmentPlans === "object") {
      if (Array.isArray(treatmentPlans)) {
        planEntries = treatmentPlans;
      } else {
        // Preserve keys to extract ruleId from "ruleId_eventId" format
        planEntries = Object.entries(treatmentPlans).map(([key, entry]) => ({
          ...entry,
          _key: key,
        }));
      }
    }

    if (planEntries.length > 0) {
      this.pdf.addParagraph(
        w1T.baselineDesc,
      );
      this.pdf.addSpace(3);

      const ruleGapComments = securityFrameworkData.ruleGapComments || {};

      const rtHeaders = w1T.baselineHeaders;
      const rtRows = [];

      planEntries.forEach((entry) => {
        // Extract ruleId from entry property or from the plan key "ruleId_eventId"
        let ruleId = entry.ruleId;
        let deId = entry.dreadedEventId || entry.id;
        if (entry._key && entry._key.includes("_")) {
          const keyParts = entry._key.split("_");
          if (!ruleId) ruleId = keyParts[0];
          if (!deId) deId = keyParts.slice(1).join("_");
        }

        // Resolve rule name from security framework selectedRules
        const rule = allRules.find(
          (r) => String(r.id || r.rule_id || r.ruleId) === String(ruleId),
        );
        const ruleName =
          rule?.name ||
          rule?.title ||
          entry.ruleName ||
          (ruleId ? `${w1T.rule} ${ruleId}` : "");

        // Rule applicability status
        const statusLevel = normalizeRuleStatus(rule?.status);
        const statusLabel = this.getRuleStatusLabel(statusLevel);

        // Rule justification
        const justification = rule?.justification || '—';

        // Find the feared event object in our main list if possible, or use what's in entry
        const de = this._findDreadedEvent(deId);
        if (!de) return; // Skip orphaned plan entries (event no longer exists, matching UI behavior)

        // Règle / Événement with event type label
        const eventName =
          de.name ||
          entry.eventName ||
          `${w1T.event} ${deId || w1T.unknown}`;
        const isNonInt = de.businessValue === '__global__';
        const dePrefix = isNonInt ? w1T.nonIntRisk : w1T.dreadedEvent;
        const colName = ruleName
          ? `${ruleName}\n${dePrefix} : ${eventName}`
          : de.shortId
            ? `${de.shortId} : ${eventName}`
            : eventName;

        // Commentaire sur l'écart with event/risk label
        const rawGap = ruleGapComments[ruleId] || '';
        const gapComment = rawGap
          ? `${w1T.commentPrefix} ${rawGap}\n\n${dePrefix} : ${eventName}`
          : `${dePrefix} : ${eventName}`;

        // Gravité (Initial) - Use exact label mapping
        const severityKey = de.severity || entry.gravity || entry.severity;
        const gravityInitial =
          severityLabels[severityKey] || this.formatSeverity(severityKey);

        // Gravité Résiduelle
        const resKey =
          entry.residualSeverity ||
          (typeof entry.residualRisk === "string"
            ? entry.residualRisk
            : entry.residualRisk?.level);
        const resGrav = severityLabels[resKey] || resKey || "-";

        // Mesures de Contrôle
        let controlsWithDetails = "-";
        if (entry.controls && entry.controls.length > 0) {
          const formattedControls = entry.controls.map((c) => {
            const controlName =
              c.name || `${w1T.controlPrefix} ${c.controlId?.substring(0, 8) || "?"}`;
            let lines = [`- ${controlName}`];
            lines.push(""); // Blank line

            if (c.decision) {
              const decLabel = decisionLabels[c.decision] || c.decision;
              let decisionText = `  • ${w1T.decisionPrefix} ${decLabel}`;
              if (c.decision === "apply_before" && c.decisionDate) {
                const dateStr = new Date(c.decisionDate)
                  .toISOString()
                  .split("T")[0];
                decisionText += ` (${dateStr})`;
              }
              lines.push(decisionText);
            }

            if (c.responsiblePerson && c.responsiblePerson !== "-") {
              lines.push(`  • ${w1T.responsiblePrefix} ${c.responsiblePerson}`);
            }

            return lines.join("\n");
          });
          controlsWithDetails = formattedControls.join("\n\n\n");
        }

        rtRows.push([
          colName,
          statusLabel,
          gapComment,
          justification,
          gravityInitial,
          controlsWithDetails,
          resGrav,
        ]);
      });

      if (rtRows.length > 0) {
        this.pdf.addTable(rtHeaders, rtRows, {
          fontSize: 7,
          columnWidths: [35, 19, 26, 21, 16, 42, 16],
        });
      } else {
        this.pdf.addParagraph(
          w1T.noPlan,
        );
      }
    } else {
      this.pdf.addParagraph(
        w1T.noBaselineData,
      );
    }
  }

  /**
   * 2.1. SOURCES DE RISQUE (Atelier 2 - Activité 1)
   */
  async generateAtelier2Sources() {
    const w2T = this.T.w2;
    this.pdf.addSectionHeading(w2T.sourcesTitle, 2);

    const definitions = {
      categoryLabels: this.T.lookups.srCategories,
      objectifViseLabels: this.T.lookups.ovCategories,
      levelLabels: this.language === 'en' ? {
        faible: "Low",
        moyen: "Medium",
        eleve: "High",
        faibles: "Low",
        moyennes: "Medium",
        importantes: "High",
      } : {
        faible: "Faible",
        moyen: "Moyen",
        eleve: "Élevé",
        faibles: "Faibles",
        moyennes: "Moyennes",
        importantes: "Importantes",
      },
    };

    const workshop2Data = this.reportData.workshops?.workshop2 || {};
    const sources = workshop2Data.sources || [];

    if (sources.length > 0) {
      this.pdf.addParagraph(
        w2T.sourcesDesc,
      );
      this.pdf.addSpace(2);

      // Group sources by category
      const sourcesByCategory = {};
      sources.forEach((source) => {
        const category = source.category || "non-categorise";
        if (!sourcesByCategory[category]) {
          sourcesByCategory[category] = [];
        }
        sourcesByCategory[category].push(source);
      });

      Object.entries(sourcesByCategory).forEach(
        ([category, categorySources]) => {
          const categoryLabel =
            definitions.categoryLabels[category] || category;
          this.pdf.addSectionHeading(`${w2T.categoryPrefix}${categoryLabel}`, 3);

          const headers = w2T.sourcesHeaders;
          const rows = categorySources.map((source) => {
            const objectifLabel =
              definitions.objectifViseLabels[source.objectifViseCategory] ||
              source.objectifViseCategory ||
              "N/A";
            const motivationLabel =
              definitions.levelLabels[source.motivation] ||
              source.motivation ||
              "N/A";
            const activiteLabel =
              definitions.levelLabels[source.activite] ||
              source.activite ||
              "N/A";
            const ressourcesLabel =
              definitions.levelLabels[source.ressources] ||
              source.ressources ||
              "N/A";
            const retenuStyle = source.selected
              ? {
                  fillColor: [220, 252, 231],
                  textColor: [22, 101, 52],
                  fontStyle: "bold",
                  halign: "center",
                }
              : {
                  fillColor: [254, 226, 226],
                  textColor: [185, 28, 28],
                  fontStyle: "bold",
                  halign: "center",
                };

            return [
              source.name || "N/A",
              source.objectifVise || "N/A",
              objectifLabel,
              motivationLabel,
              activiteLabel,
              ressourcesLabel,
              { content: source.selected ? this.T.common.yes : this.T.common.no, styles: retenuStyle },
            ];
          });

          this.pdf.addTable(headers, rows, {
            fontSize: 8,
            columnWidths: [35, 45, 25, 18, 18, 18, 15],
          });
          this.pdf.addSpace(3);
        },
      );
    } else {
      this.pdf.addParagraph(
        w2T.noSources,
      );
    }
    this.pdf.addSpace(5);
  }

  /**
   * 2.2. ASSOCIATIONS SR/ER (Atelier 2 - Activité 3)
   */
  async generateAtelier2Associations() {
    const w2T = this.T.w2;
    this.pdf.addSectionHeading(w2T.assocTitle, 2);

    const definitions = {
      severityColors: {
        minor: { bg: [220, 252, 231], text: [22, 101, 52], label: this.language === 'en' ? "Minor" : "Mineur" },
        moderate: {
          bg: [254, 249, 195],
          text: [161, 98, 7],
          label: this.language === 'en' ? "Moderate" : "Modéré",
        },
        major: { bg: [255, 237, 213], text: [194, 65, 12], label: this.language === 'en' ? "Major" : "Majeur" },
        critical: {
          bg: [254, 226, 226],
          text: [185, 28, 28],
          label: this.language === 'en' ? "Critical" : "Critique",
        },
        catastrophic: {
          bg: [243, 232, 255],
          text: [107, 33, 168],
          label: this.language === 'en' ? "Catastrophic" : "Catastrophique",
        },
      },
      niveauColors: {
        1: { bg: [209, 250, 229], text: [6, 95, 70], label: this.language === 'en' ? "Level 1" : "Niveau 1" },
        2: { bg: [254, 243, 199], text: [146, 64, 14], label: this.language === 'en' ? "Level 2" : "Niveau 2" },
        3: { bg: [254, 226, 226], text: [185, 28, 28], label: this.language === 'en' ? "Level 3" : "Niveau 3" },
      },
    };

    const workshop2Data = this.reportData.workshops?.workshop2 || {};
    const sources = workshop2Data.sources || [];
    // Mappings might be in workshop2Data.mappings OR in main report mappings depending on structure
    const rawMappings =
      workshop2Data.mappings || this.reportData.mappings || [];
    // Get couples SR/OV data for niveau, selected, and justification
    const couplesSROV =
      workshop2Data.couplesSROV || workshop2Data.couples || [];

    // ENRICH DATA LOGIC (Matching Activite3.js)
    const enrichedMappings = rawMappings.map((mapping) => {
      // If data is already enriched, use it. Otherwise, look it up.
      if (mapping.sourceName && mapping.sourceMotivation && mapping.eventName) {
        return mapping;
      }

      const source = sources.find((s) => s.id === mapping.sourceId);
      const event = this._findDreadedEvent(mapping.dreadedEventId);

      return {
        ...mapping,
        sourceName: source?.name || mapping.sourceName || "N/A",
        sourceObjective:
          source?.objectifVise || mapping.sourceObjective || "N/A",
        sourceMotivation:
          source?.motivation || mapping.sourceMotivation || "N/A",
        sourceActivity: source?.activite || mapping.sourceActivity || "N/A",
        sourceResources: source?.ressources || mapping.sourceResources || "N/A",
        eventName: event?.name || mapping.eventName || "N/A",
        eventPillar: event?.securityPillar || mapping.eventPillar || "N/A",
        eventSeverity: event?.severity || mapping.eventSeverity || "N/A",
        eventImpacts: event?.impacts
          ? event.impacts
              .map((i) => (typeof i === "string" ? i : i.impact || String(i)))
              .join(", ")
          : mapping.eventImpacts || "N/A",
      };
    });

    if (enrichedMappings.length > 0) {
      this.pdf.addParagraph(
        w2T.assocDesc,
      );
      this.pdf.addSpace(2);

      const headers = w2T.assocHeaders;

      const rows = enrichedMappings.map((mapping) => {
        // Find the source to get selected and justification
        const source = sources.find((s) => s.id === mapping.sourceId);

        // Find matching couple SR/OV for additional data
        const couple = couplesSROV.find((c) => c.sourceId === mapping.sourceId);

        // Calculate niveau (threat level)
        const niveau =
          couple?.niveau ||
          this.calculateNiveau(
            mapping.sourceMotivation,
            mapping.sourceActivity,
            mapping.sourceResources,
          );

        // Get selected (retenu) status - from couple first, then source
        const isRetained =
          couple?.selected !== undefined
            ? couple.selected
            : source?.selected || false;

        // Get justification - from couple first, then source
        const justification =
          couple?.justification ||
          source?.justification ||
          (isRetained ? w2T.noJustification : "-");

        // Format SR Capacities (Motivation, Activity, Resources)
        const capacities = [
          `M: ${mapping.sourceMotivation || "N/A"}`,
          `A: ${mapping.sourceActivity || "N/A"}`,
          `R: ${mapping.sourceResources || "N/A"}`,
        ].join("\n");

        // Merge Pilier into Événement Redouté
        const eventWithPillar = mapping.eventPillar
          ? `${mapping.eventName || "N/A"}\n(${this.T.lookups.pillars[mapping.eventPillar] || mapping.eventPillar})`
          : mapping.eventName || "N/A";

        // Niveau styling
        const niveauObj = definitions.niveauColors[niveau];
        const niveauStyle = niveauObj
          ? {
              fillColor: niveauObj.bg,
              textColor: niveauObj.text,
              fontStyle: "bold",
              halign: "center",
            }
          : { halign: "center" };

        // Retenu styling (green "Oui" / "Yes" for selected, red "Non" / "No" for not selected)
        const retenuStyle = isRetained
          ? {
              fillColor: [220, 252, 231],
              textColor: [22, 101, 52],
              fontStyle: "bold",
              halign: "center",
            }
          : {
              fillColor: [254, 226, 226],
              textColor: [185, 28, 28],
              fontStyle: "bold",
              halign: "center",
            };

        return [
          mapping.sourceName || "N/A",
          mapping.sourceObjective || "N/A",
          { content: eventWithPillar, styles: { fontSize: 6 } },
          { content: String(niveau), styles: niveauStyle },
          { content: capacities, styles: { fontSize: 6 } },
          { content: isRetained ? this.T.common.yes : this.T.common.no, styles: retenuStyle },
          { content: justification, styles: { fontSize: 6 } },
        ];
      });

      this.pdf.addTable(headers, rows, {
        fontSize: 7,
        columnWidths: [28, 34, 24, 15, 22, 12, 32],
      });
    } else {
      this.pdf.addParagraph(
        w2T.noAssoc,
      );
    }
    this.pdf.addSpace(5);
  }

  /**
   * Add stakeholder radar visualization image to PDF
   */
  async addStakeholderRadarVisualization() {
    // Robust fallback for analysisId
    const analysisId = this.analysisId || this.reportData?.analysisId;

    if (!analysisId) {
      console.warn("⚠️ No analysisId found for radar visualization");
      this.pdf.addParagraph(
        this.language === 'en'
          ? "Note: Missing analysis ID for radar visualization."
          : "Note: ID d'analyse manquant pour la visualisation radar.",
      );
      return;
    }

    try {
      console.log(
        "📸 Attempting to retrieve stakeholder radar visualization for Matrix Report...",
      );
      const stakeholderService = await import("../services/stakeholderService");
      const service = stakeholderService.default || stakeholderService;

      if (service && service.getStakeholderRadarImage) {
        const response = await service.getStakeholderRadarImage(analysisId);

        // Try to locate imageData in various common paths
        let imageDataUrl = null;
        if (response?.data?.imageData) {
          imageDataUrl = response.data.imageData;
        } else if (response?.data?.data?.imageData) {
          imageDataUrl = response.data.data.imageData;
        } else if (response?.imageData) {
          imageDataUrl = response.imageData;
        }

        if (imageDataUrl) {
          // Use specific margins for EnhancedPDFGenerator
          const margin = this.pdf.margin || 20;

          // Add description
          this.pdf.addParagraph(
            this.T.w3.radarDesc,
          );
          this.pdf.addSpace(5);

          // Image handling
          const img = new Image();
          img.src = imageDataUrl;

          await new Promise((resolve, reject) => {
            img.onload = () => {
              try {
                const maxWidth = 170;
                const maxHeight = 120;
                let imgWidth = img.width;
                let imgHeight = img.height;

                const widthRatio = maxWidth / imgWidth;
                const heightRatio = maxHeight / imgHeight;
                const ratio = Math.min(widthRatio, heightRatio);

                imgWidth = imgWidth * ratio;
                imgHeight = imgHeight * ratio;

                const pageWidth = this.pdf.pageWidth || 210;
                const xPos = (pageWidth - imgWidth) / 2;

                // Check page break using manual check since we use raw addImage
                if (
                  this.pdf.currentY + imgHeight >
                  (this.pdf.pageHeight || 297) - margin
                ) {
                  this.pdf.pdf.addPage();
                  this.pdf.currentY = margin;
                }

                this.pdf.pdf.addImage(
                  imageDataUrl,
                  "PNG",
                  xPos,
                  this.pdf.currentY,
                  imgWidth,
                  imgHeight,
                );
                this.pdf.currentY += imgHeight + 10;

                console.log("✅ Stakeholder radar image added to Matrix PDF");
                resolve();
              } catch (e) {
                reject(e);
              }
            };
            img.onerror = (e) => reject(e);
          });
        } else {
          console.warn(
            "⚠️ No radar image data found via any path. Response keys:",
            Object.keys(response || {}),
          );
          if (response?.data)
            console.warn("Response.data keys:", Object.keys(response.data));

          this.pdf.addParagraph(
            this.language === 'en'
              ? "Note: No radar visualization data available (Unexpected response structure)."
              : "Note: Aucune donnée de visualisation radar disponible (Structure de réponse inattendue).",
          );
        }
      } else {
        console.warn(
          "⚠️ Stakeholder service unavailable or getStakeholderRadarImage not found.",
        );
        this.pdf.addParagraph(
          this.language === 'en'
            ? "Note: Visualization service unavailable."
            : "Note: Service de visualisation indisponible.",
        );
      }
    } catch (error) {
      console.error("Error adding radar visualization:", error);
      this.pdf.addParagraph(
        this.language === 'en'
          ? "Note: Error loading radar visualization."
          : "Note: Erreur lors du chargement de la visualisation radar.",
      );
    }
  }

  async generateAtelier3Ecosystem() {
    const w3T = this.T.w3;
    this.pdf.addSectionHeading(
      w3T.ecoTitle,
      2,
    );

    // Fix: backend sends stakeholders in workshops.workshop3.stakeholders
    const rawStakeholders =
      this.reportData.workshops?.workshop3?.stakeholders ||
      this.reportData.stakeholders ||
      [];
    const stakeholders = rawStakeholders.filter(
      (s) => !s.isCategory && !s.deleted && s.name && s.name !== "N/A",
    );

    const categoryLabels = this.T.lookups.stakeholderCategories;

    // 3.1.1. Description
    this.pdf.addSectionHeading(w3T.stakeholdersTitle, 3);

    if (stakeholders.length > 0) {
      const descriptionData = stakeholders.map((stakeholder, index) => {
        const catLabel = categoryLabels[stakeholder.category] || stakeholder.category || "N/A";
        const typeLabel =
          stakeholder.type === "internal"
            ? this.T.lookups.stakeholderTypes.internal
            : this.T.lookups.stakeholderTypes.external;
        const description =
          stakeholder.description || (this.language === 'en' ? "Not specified" : "Non spécifiée");
        const truncatedDesc =
          description.length > 100
            ? description.substring(0, 97) + "..."
            : description;

        return [
          (index + 1).toString(),
          stakeholder.name || "N/A",
          catLabel,
          typeLabel,
          truncatedDesc,
        ];
      });

      this.pdf.addTable(
        w3T.stakeholdersHeaders,
        descriptionData,
        {
          columnWidths: [10, 35, 25, 20, 80],
          styles: {
            fontSize: 7,
            cellPadding: 2,
            lineColor: [226, 232, 240],
            lineWidth: 0.5,
            overflow: "linebreak",
            valign: "top",
          },
          headStyles: {
            fillColor: [30, 64, 175],
            textColor: [255, 255, 255],
            fontSize: 8,
            fontStyle: "bold",
            halign: "left",
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252],
          },
        },
      );
    } else {
      this.pdf.addParagraph(w3T.noStakeholders);
    }
    this.pdf.addSpace(5);

    // 3.1.2. ÉVALUATION DES PARTIES PRENANTES
    this.pdf.addSectionHeading(
      w3T.evalTitle,
      3,
    );

    // Thresholds
    const thresholds = this.reportData.thresholds || {
      danger: 3,
      control: 1.5,
      watch: 0.5,
    };
    const zones = this.T.lookups.threatZones;
    const getZoneLabel = (threatLevelValue) => {
      if (threatLevelValue >= thresholds.danger) return zones.danger;
      if (threatLevelValue >= thresholds.control) return zones.control;
      if (threatLevelValue >= thresholds.watch) return zones.watch;
      return zones.outOfScope;
    };

    if (stakeholders.length > 0) {
      const evaluationData = stakeholders.map((stakeholder) => {
        const dependency = Number(stakeholder.dependency) || 1;
        const penetration = Number(stakeholder.penetration) || 1;
        const maturity =
          Number(stakeholder.maturity) ||
          Number(stakeholder.cyberMaturity) ||
          1;
        const trust =
          Number(stakeholder.trust) || Number(stakeholder.trustLevel) || 1;

        const denominator = maturity * trust;
        const threatLevelValue =
          denominator > 0 ? (dependency * penetration) / denominator : 0;
        const threatLevel = threatLevelValue.toFixed(2);
        const retainedLabel = stakeholder.retained ? this.T.common.yes : this.T.common.no;
        const zoneLabel = getZoneLabel(threatLevelValue);

        let zoneStyle = { halign: "center" };
        if (zoneLabel === zones.danger) {
          zoneStyle = {
            fillColor: [254, 226, 226],
            textColor: [185, 28, 28],
            fontStyle: "bold",
            halign: "center",
          };
        } else if (zoneLabel === zones.control) {
          zoneStyle = {
            fillColor: [254, 249, 195],
            textColor: [161, 98, 7],
            fontStyle: "bold",
            halign: "center",
          };
        } else if (zoneLabel === zones.watch) {
          zoneStyle = {
            fillColor: [220, 252, 231],
            textColor: [22, 101, 52],
            halign: "center",
          };
        } else if (zoneLabel === zones.outOfScope) {
          zoneStyle = {
            fillColor: [241, 245, 249],
            textColor: [100, 116, 139],
            halign: "center",
          };
        }

        let retainedStyle = { halign: "center" };
        if (retainedLabel === this.T.common.yes) {
          retainedStyle = {
            fillColor: [220, 252, 231],
            textColor: [22, 101, 52],
            fontStyle: "bold",
            halign: "center",
          };
        }

        return [
          {
            content: stakeholder.name || "N/A",
            styles: { fontStyle: "bold", halign: "left" },
          },
          dependency.toString(),
          penetration.toString(),
          maturity.toString(),
          trust.toString(),
          { content: retainedLabel, styles: retainedStyle },
          threatLevel,
          { content: zoneLabel, styles: zoneStyle },
        ];
      });

      this.pdf.addTable(
        w3T.evalHeaders,
        evaluationData,
        {
          columnWidths: [37, 16, 16, 16, 16, 16, 20, 38],
          styles: {
            fontSize: 7,
            cellPadding: 2,
            lineColor: [226, 232, 240],
            lineWidth: 0.5,
            valign: "middle",
            halign: "center",
          },
          headStyles: {
            fillColor: [30, 64, 175],
            textColor: [255, 255, 255],
            fontSize: 7,
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252],
          },
        },
      );
    } else {
      this.pdf.setFontSize(9);
      this.pdf.setTextColor(148, 163, 184);
      this.pdf.setFont("helvetica", "italic");
      this.pdf.addParagraph(w3T.noEval);
      this.pdf.setTextColor(0, 0, 0);
      this.pdf.setFont("helvetica", "normal");
    }
    this.pdf.addSpace(5);

    // 3.1.3. RISQUE RÉSIDUEL PAR PARTIE PRENANTE
    this.pdf.addSectionHeading(
      w3T.resStakeholderTitle,
      3,
    );

    const retainedStakeholders = stakeholders.filter(s => s.retained !== false);
    if (retainedStakeholders.length > 0) {
      const getRiskZoneLabel = (threatLevel) => {
        if (threatLevel >= 3) return zones.danger;
        if (threatLevel >= 1.5) return zones.control;
        if (threatLevel >= 0.5) return zones.watch;
        return zones.outOfScope;
      };

      const mappings =
        this.reportData.workshops?.workshop2?.mappings ||
        this.reportData.mappings ||
        [];
      const sourcesRisque =
        this.reportData.workshops?.workshop2?.sources ||
        this.reportData.sourcesRisque ||
        [];
      const ecosystemMeasures =
        this.reportData.workshops?.workshop3?.ecosystemMeasures ||
        this.reportData.ecosystemMeasures ||
        [];

      // Build attack paths from mappings
      let globalCounter = 1;
      const attackPathsData = [];
      mappings.forEach((mapping, index) => {
        const scenarioNumber = index + 1;
        const source = sourcesRisque.find((s) => s.id === mapping.sourceId);
        const event = this._findDreadedEvent(mapping.dreadedEventId);
        if (!source || !event) return;

        attackPathsData.push({
          id: `${mapping.sourceId}-${mapping.dreadedEventId}-direct`,
          type: "direct",
          referenceCode: `CAD${scenarioNumber.toString().padStart(2, "0")}`,
          sourceRiskId: mapping.sourceId,
          sourceRiskName: source.name,
          dreadedEventId: mapping.dreadedEventId,
          stakeholders: []
        });

        const scenarioKey = `${mapping.sourceId}-${mapping.dreadedEventId}`;
        const concernedStakeholders = stakeholders.filter(
          (s) =>
            s.retained &&
            Array.isArray(s.concernedScenarios) &&
            s.concernedScenarios.includes(scenarioKey),
        );

        concernedStakeholders.forEach((stakeholder) => {
          attackPathsData.push({
            id: `${mapping.sourceId}-${mapping.dreadedEventId}-${stakeholder.id}`,
            type: "stakeholder",
            referenceCode: `CA${(globalCounter++).toString().padStart(2, "0")}`,
            sourceRiskId: mapping.sourceId,
            sourceRiskName: source.name,
            dreadedEventId: mapping.dreadedEventId,
            stakeholders: [stakeholder.id]
          });
        });
      });

      const residualRiskRows = retainedStakeholders.map((stakeholder) => {
        const initialD = stakeholder.dependency || 1;
        const initialP = stakeholder.penetration || 1;
        const initialM = stakeholder.cyberMaturity || 1;
        const initialC = stakeholder.trust || 1;
        const initialRisk = (initialD * initialP) / (initialM * initialC);

        const relatedPathIds = new Set(
          attackPathsData
            .filter((path) => {
              if (!path.stakeholders || path.stakeholders.length === 0) return false;
              return path.stakeholders.some((sid) => sid === stakeholder.id || sid === stakeholder.name);
            })
            .map((p) => p.id)
        );

        const relatedMeasures = ecosystemMeasures.filter((m) => {
          const mPaths = m.attackPaths || (m.attackPathId ? [m.attackPathId] : []);
          return mPaths.some((pid) => relatedPathIds.has(pid));
        });

        let currentD = initialD;
        let currentP = initialP;
        let currentM = initialM;
        let currentC = initialC;

        relatedMeasures.forEach((m) => {
          const factor = m.implementation === "abandoned" ? 0 : 1;
          currentD -= (m.dependanceEffectiveness || 0) * factor;
          currentP -= (m.penetrationEffectiveness || 0) * factor;
          currentM += (m.maturiteCyberEffectiveness || 0) * factor;
          currentC += (m.confianceEffectiveness || 0) * factor;
        });

        currentD = Math.max(1, Math.min(4, currentD));
        currentP = Math.max(1, Math.min(4, currentP));
        currentM = Math.max(1, Math.min(4, currentM));
        currentC = Math.max(1, Math.min(4, currentC));

        const residualRisk = (currentD * currentP) / (currentM * currentC);
        const reduction = initialRisk > 0 ? (initialRisk - residualRisk) / initialRisk : 0;

        return {
          stakeholderName: stakeholder.name || "N/A",
          initialRisk,
          initialZone: getRiskZoneLabel(initialRisk),
          residualRisk,
          residualZone: getRiskZoneLabel(residualRisk),
          reduction: Math.round(reduction * 100),
          residualRiskExpression: stakeholder.residualRiskExpression || "",
        };
      });

      const rrHeaders = w3T.resStakeholderHeaders;
      const rrData = residualRiskRows.map((r) => [
        r.stakeholderName,
        r.initialRisk.toFixed(2),
        r.initialZone,
        r.residualRisk.toFixed(2),
        r.residualRiskExpression,
        r.residualZone,
        `-${r.reduction}%`,
      ]);

      this.pdf.addTable(rrHeaders, rrData, {
        columnWidths: [28, 18, 25, 20, 45, 25, 15],
        styles: {
          fontSize: 7,
          cellPadding: 2,
          lineColor: [226, 232, 240],
          lineWidth: 0.5,
          overflow: "linebreak",
          valign: "top",
          halign: "center",
        },
        headStyles: {
          fillColor: [30, 64, 175],
          textColor: [255, 255, 255],
          fontSize: 7,
          fontStyle: "bold",
        },
        columnStyles: {
          0: { halign: "left", fontStyle: "bold" },
          4: { halign: "left" },
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
      });
    } else {
      this.pdf.addParagraph(
        w3T.noRetainedStakeholders,
      );
    }
    this.pdf.addSpace(5);

    // 3.1.4. REPRÉSENTATION DE LA CARTOGRAPHIE
    this.pdf.addSectionHeading(
      w3T.radarTitle,
      3,
    );
    await this.addStakeholderRadarVisualization();
    this.pdf.addSpace(5);
  }

  /**
   * 3.2. SCÉNARIOS STRATÉGIQUES (Atelier 3 - Activité 2)
   */
  async generateAtelier3Scenarios() {
    const w3T = this.T.w3;
    this.pdf.addSectionHeading(w3T.scenariosTitle, 2);

    const mappings =
      this.reportData.mappings ||
      this.reportData.workshops?.workshop2?.mappings ||
      [];
    const sourcesRisque =
      this.reportData.workshops?.workshop2?.sources ||
      this.reportData.sourcesRisque ||
      [];
    const controlsData = this.reportData.analysisControls || {};
    const stakeholders = this.reportData.stakeholders || [];
    const businessValues =
      this.reportData.workshops?.workshop1?.businessValues ||
      this.reportData.businessValues ||
      [];
    const selectedStakeholders =
      this.reportData.workshops?.workshop3?.selectedStakeholders || {};

    if (mappings.length === 0) {
      this.pdf.addParagraph(
        w3T.noScenarios,
      );
      return;
    }

    // Processing Logic from StrategicScenariosPdfGenerator
    const processedData = mappings
      .map((mapping) => {
        const sourceRisk = sourcesRisque.find(
          (source) => source.id === mapping.sourceId,
        );
        const dreadedEvent = this._findDreadedEvent(mapping.dreadedEventId);

        if (!sourceRisk || !dreadedEvent) return null;

        const businessValue = businessValues.find(
          (bv) => String(bv.id) === String(dreadedEvent.businessValue),
        );

        // Get controls
        const eventControls = [];
        const planDataToCheck = controlsData.plan || controlsData;
        if (planDataToCheck) {
          const planEntries = Array.isArray(planDataToCheck)
            ? planDataToCheck
            : Object.entries(planDataToCheck).map(([key, val]) => ({
                ...val,
                _key: key,
              }));
          planEntries.forEach((entry) => {
            const deId = entry.dreadedEventId || entry.id;
            if (deId === dreadedEvent.id) {
              if (entry.controls && Array.isArray(entry.controls)) {
                eventControls.push(...entry.controls);
              }
            }
          });
        }

        // Stakeholders
        const key = `${mapping.sourceId}-${mapping.dreadedEventId}`;
        const selectedIds = selectedStakeholders[key] || [];
        const itemStakeholders = stakeholders
          .filter((s) => selectedIds.includes(s.id))
          .map((s) => s.name);

        return {
          id: mapping.id,
          sourceRisk: sourceRisk.name,
          sourceCategory: this.T.lookups.srCategories[sourceRisk.category] || sourceRisk.category,
          objectifVise: sourceRisk.objectifVise,
          objectifViseCategory: this.T.lookups.ovCategories[sourceRisk.objectifViseCategory] || sourceRisk.objectifViseCategory,
          dreadedEvent: dreadedEvent.name,
          controls: eventControls,
          businessValue: businessValue ? businessValue.name : "N/A",
          supportAssets: businessValue ? businessValue.supportAssets || [] : [],
          selectedStakeholders: itemStakeholders,
        };
      })
      .filter(Boolean);

    // Sort data
    processedData.sort((a, b) => {
      return (
        a.dreadedEvent.localeCompare(b.dreadedEvent) ||
        a.sourceRisk.localeCompare(b.sourceRisk)
      );
    });

    // Add IDs
    processedData.forEach((item, index) => {
      item.scenarioNumber = index + 1;
    });

    if (processedData.length > 0) {
      const tableBody = processedData.map((item) => {
        // Controls Text
        const controlsText =
          item.controls.length > 0
            ? item.controls
                .map((c) => `- ${c.name || c.controlName}`)
                .join("\n")
            : (this.language === 'en' ? "No measures defined" : "Aucune mesure définie");

        // Assets Text
        const assetsText =
          item.supportAssets.length > 0
            ? item.supportAssets
                .map((a) => `[${a.shortId || "?"}] ${a.name}`)
                .join("\n")
            : (this.language === 'en' ? "No supporting assets" : "Aucun bien support");

        // Stakeholders Text
        const stakeholdersText =
          item.selectedStakeholders.length > 0
            ? item.selectedStakeholders.join("\n")
            : (this.language === 'en' ? "None" : "Aucune");

        return [
          `S${String(item.scenarioNumber).padStart(2, "0")}`,
          `${item.sourceRisk}\n(${item.sourceCategory || "-"})`,
          `${item.objectifVise}\n(${item.objectifViseCategory || "-"})`,
          item.dreadedEvent,
          `${item.businessValue}\n\n${assetsText}`,
          controlsText,
        ];
      });

      const headers = w3T.scenariosHeaders;

      this.pdf.addTable(headers, tableBody, {
        columnWidths: [12, 25, 22, 24, 35, 47],
        fontSize: 7,
      });
    } else {
      this.pdf.addParagraph(
        w3T.noCompleteScenarios,
      );
    }
  }

  /**
   * Helper: Format severity
   */
  formatSeverity(level) {
    if (!level) return "-";
    const map = this.language === 'en' ? {
      1: "Minor (1)",
      2: "Moderate (2)",
      3: "Major (3)",
      4: "Critical (4)",
      5: "Catastrophic (5)",
    } : {
      1: "Mineure (1)",
      2: "Modérée (2)",
      3: "Majeure (3)",
      4: "Critique (4)",
      5: "Catastrophique (5)",
    };
    return map[level] || level;
  }

  _findDreadedEvent(deId) {
    if (!deId) return null;
    const id = String(deId);
    const suffix = id.includes('-') ? id.split('-').slice(1).join('-') : null;
    const events = this.reportData.workshops?.workshop1?.dreadedEvents || [];
    return events.find(d => {
      const dId = String(d.id);
      return dId === id ||
        String(d._id) === id ||
        dId.endsWith(id) ||
        (suffix && (dId === suffix || dId.endsWith(suffix)));
    }) || null;
  }

  /**
   * 3.3. CHEMINS D'ATTAQUE (Atelier 3 - Activité 3)
   */
  async generateAtelier3AttackPaths() {
    const w3T = this.T.w3;
    this.pdf.addSectionHeading(w3T.attackPathsTitle, 2);

    const strategicScenarios =
      this.reportData.workshops?.workshop5?.strategicScenarios || [];
    const seenIds = new Set();
    const attackPaths = [];
    strategicScenarios.forEach((ss) => {
      (ss.attackPaths || []).forEach((ap) => {
        if (!ap || seenIds.has(ap.id)) return;
        seenIds.add(ap.id);
        attackPaths.push({ ...ap, scenario: ss });
      });
    });

    if (attackPaths.length === 0) {
      this.pdf.addParagraph(
        w3T.noAttackPaths,
      );
      return;
    }

    const tableBody = attackPaths.map((path) => {
      const stakeholdersText =
        path.stakeholders && path.stakeholders.length > 0
          ? path.stakeholders.map((s) => s.name).join("\n")
          : (this.language === 'en' ? "None" : "Aucune");

      const vraisemblanceText = path.preliminary?.active
        ? `V${path.preliminary.likelihood} · ${w3T.preliminaryStudy}`
        : w3T.toEvaluateW4;

      return [
        path.strategicScenarioReference || "-",
        path.referenceCode || "-",
        path.sourceRiskName || "",
        path.objectifVise || "",
        path.dreadedEventName || "",
        path.businessValueName || "",
        stakeholdersText,
        vraisemblanceText,
      ];
    });

    this.pdf.addTable(
      w3T.attackPathsHeaders,
      tableBody,
      {
        fontSize: 7,
        columnWidths: [12, 12, 24, 24, 30, 24, 30, 14],
      },
    );

    this.pdf.addParagraph(
      w3T.attackPathNote,
    );

    this.pdf.addSpace(5);
  }

  /**
   * 3.4. MESURES DE SÉCURITÉ (Atelier 3 - Activité 4)
   */
  async generateAtelier3SecurityMeasures() {
    const w3T = this.T.w3;
    this.pdf.addNewPage();
    this.pdf.addSectionHeading(w3T.measuresTitle, 2);

    const measures =
      this.reportData.workshops?.workshop3?.ecosystemMeasures ||
      this.reportData.workshops?.workshop3?.measures ||
      [];

    if (measures.length === 0) {
      this.pdf.addParagraph(
        w3T.noMeasures,
      );
      return;
    }

    const mappings =
      this.reportData.workshops?.workshop2?.mappings ||
      this.reportData.mappings ||
      [];
    const sourcesRisque =
      this.reportData.workshops?.workshop2?.sources ||
      this.reportData.sourcesRisque ||
      [];
    const stakeholders =
      this.reportData.workshops?.workshop3?.stakeholders ||
      this.reportData.stakeholders ||
      [];

    let allGeneratedAttackPaths = [];
    let globalStakeholderAttackPathCounter = 1;

    mappings.forEach((mapping, index) => {
      const scenarioNumber = index + 1;
      const source = sourcesRisque.find((s) => s.id === mapping.sourceId);
      const event = this._findDreadedEvent(mapping.dreadedEventId);

      if (!source || !event) return;

      allGeneratedAttackPaths.push({
        id: `${mapping.sourceId}-${mapping.dreadedEventId}-direct`,
        type: "direct",
        name: this.language === 'en' ? `Direct attack - ${source.name}` : `Attaque directe - ${source.name}`,
        referenceCode: `CAD${scenarioNumber.toString().padStart(2, "0")}`,
        sourceRiskId: mapping.sourceId,
        sourceRiskName: source.name,
        dreadedEventId: mapping.dreadedEventId,
        dreadedEventName: event.name,
        objectifVise: source.objectifVise || "",
      });

      const scenarioKey = `${mapping.sourceId}-${mapping.dreadedEventId}`;
      const concernedStakeholders = stakeholders.filter(
        (s) =>
          s.retained &&
          Array.isArray(s.concernedScenarios) &&
          s.concernedScenarios.includes(scenarioKey),
      );

      concernedStakeholders.forEach((stakeholder) => {
        allGeneratedAttackPaths.push({
          id: `${mapping.sourceId}-${mapping.dreadedEventId}-${stakeholder.id}`,
          type: "stakeholder",
          name: this.language === 'en' ? `Attack via ${stakeholder.name}` : `Attaque via ${stakeholder.name}`,
          referenceCode: `CA${(globalStakeholderAttackPathCounter++).toString().padStart(2, "0")}`,
          sourceRiskId: mapping.sourceId,
          sourceRiskName: source.name,
          dreadedEventId: mapping.dreadedEventId,
          dreadedEventName: event.name,
          objectifVise: source.objectifVise || "",
        });
      });
    });

    const attackPathsData = allGeneratedAttackPaths;

    const getAssociatedAttackPaths = (measure) => {
      if (!measure || attackPathsData.length === 0) return [];

      const attackPathMap = new Map();
      attackPathsData.forEach((path) => {
        if (path.id) {
          attackPathMap.set(path.id, path);
        }
      });

      const matchedPaths = [];

      if (measure.attackPaths && Array.isArray(measure.attackPaths)) {
        measure.attackPaths.forEach((measurePathId) => {
          const path = attackPathMap.get(measurePathId);
          if (path && !matchedPaths.find((p) => p.id === path.id)) {
            matchedPaths.push(path);
          }
        });
      }

      if (measure.attackPathId) {
        const path = attackPathMap.get(measure.attackPathId);
        if (path && !matchedPaths.find((p) => p.id === path.id)) {
          matchedPaths.push(path);
        }
      }

      return matchedPaths;
    };

    const enrichedMeasures = measures.map((measure) => ({
      ...measure,
      attackPathsDetails: getAssociatedAttackPaths(measure),
    }));

    const CATEGORIES = this.T.lookups.measureCategories;
    const PRIORITIES = this.language === 'en' ? {
      high: "High",
      medium: "Medium",
      low: "Low",
    } : {
      high: "Haute",
      medium: "Moyenne",
      low: "Basse",
    };

    const STATUSES = this.language === 'en' ? {
      planned: "Planned",
      "in-progress": "In progress",
      implemented: "Implemented",
      abandoned: "Abandoned",
    } : {
      planned: "Planifiée",
      "in-progress": "En cours",
      implemented: "Implémentée",
      abandoned: "Abandonnée",
    };

    // Sort measures by category then title
    const sortedMeasures = [...enrichedMeasures].sort((a, b) => {
      const catCompare = (a.category || "").localeCompare(b.category || "");
      if (catCompare !== 0) return catCompare;
      return (a.title || "").localeCompare(b.title || "");
    });

    const tableBody = sortedMeasures.map((measure) => {
      const pathsText =
        measure.attackPathsDetails && measure.attackPathsDetails.length > 0
          ? measure.attackPathsDetails
              .map(
                (p) =>
                  `[${p.referenceCode}] ${p.sourceRiskName || (this.language === 'en' ? "Unknown source" : "Source inconnue")}`,
              )
              .join("\n")
          : (this.language === 'en' ? "No associated path" : "Aucun chemin associé");

      const details = [];
      if (measure.responsibleParty)
        details.push(`Resp: ${measure.responsibleParty}`);
      if (measure.deadline)
        details.push(
          `${this.language === 'en' ? 'Due:' : 'Éch:'} ${new Date(measure.deadline).toLocaleDateString(this.language === 'en' ? 'en-GB' : 'fr-FR')}`,
        );
      if (measure.effectiveness)
        details.push(`Effic: ${measure.effectiveness}/5`);
      const detailsText = details.join("\n");

      const categoryLabel = "Cat:";
      const priorityLabel = "Prio:";
      const statusLabel = "Stat:";
      const detailsLabel = "Dét:";

      const categoryValue =
        CATEGORIES[measure.category] || measure.category || (this.language === 'en' ? "Other" : "Autre");
      const priorityValue =
        PRIORITIES[measure.priority] || measure.priority || (this.language === 'en' ? "Medium" : "Moyenne");
      const statusValue =
        STATUSES[measure.implementation] ||
        measure.implementation ||
        (this.language === 'en' ? "Undefined" : "Non défini");

      const mergedInfoText = [
        `${categoryLabel} ${categoryValue}`,
        `${priorityLabel} ${priorityValue}`,
        `${statusLabel} ${statusValue}`,
        detailsText ? `${detailsLabel} ${detailsText}` : "",
      ]
        .filter(Boolean)
        .join("\n");

      return [
        measure.title || "",
        measure.description || "",
        mergedInfoText,
        pathsText,
      ];
    });

    this.pdf.addTable(
      w3T.measuresHeaders,
      tableBody,
      {
        columnWidths: [38, 55, 35, 45],
        fontSize: 8,
        headStyles: {
          fillColor: [30, 64, 175],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: "bold",
          halign: "center",
          valign: "middle",
        },
        styles: {
          fontSize: 8,
          cellPadding: 3,
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
          overflow: "linebreak",
          valign: "top",
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
      },
    );
  }

  /**
   * 3.5. Plan de traitement de risque atelier 3
   * Shows the progression of measures for each feared event
   */
  async generateAtelier3EventProgression() {
    const w3T = this.T.w3;
    this.pdf.addNewPage();
    this.pdf.addSectionHeading(
      w3T.progressionTitle,
      2,
    );
    this.pdf.addParagraph(
      w3T.progressionDesc,
    );
    this.pdf.addSpace(2);

    const eventProgression =
      this.reportData.workshops?.workshop3?.eventProgression || [];
    const dreadedEvents =
      this.reportData.workshops?.workshop1?.dreadedEvents || [];
    const businessValues =
      this.reportData.workshops?.workshop1?.businessValues || [];

    let events = eventProgression;

    if (events.length === 0 && dreadedEvents.length > 0) {
      const controlsData = this.reportData.analysisControls || {};
      const planData = controlsData.plan || controlsData;
      const a3Measures =
        this.reportData.workshops?.workshop3?.ecosystemMeasures ||
        this.reportData.workshops?.workshop3?.measures ||
        [];

      events = dreadedEvents.map((de) => {
        const a1Measures = [];
        if (planData) {
          const planEntries = Array.isArray(planData)
            ? planData
            : Object.values(planData);
          planEntries.forEach((entry) => {
            const deId = entry.dreadedEventId || entry.id;
            const matchesDe = deId && (
              String(deId) === String(de.id) ||
              String(deId) === String(de._id) ||
              String(deId) === String(de.eventId)
            );
            if (
              matchesDe &&
              entry.controls &&
              Array.isArray(entry.controls)
            ) {
              a1Measures.push(
                ...entry.controls.map(
                  (c) => c.name || c.controlName || (this.language === 'en' ? "Unnamed measure" : "Mesure sans nom"),
                ),
              );
            }
          });
        }

        const bv = businessValues.find(
          (b) => b.id === de.businessValue || b.id === de.businessValueId,
        );

        const totalMeasures = a1Measures.length + a3Measures.length;
        const progressionRate =
          totalMeasures > 0
            ? Math.round((a1Measures.length / totalMeasures) * 100)
            : 0;

        return {
          name: de.name,
          gravity: this.formatSeverity(de.severity),
          securityPillar: this.T.lookups.pillars[de.securityPillar] || de.securityPillar || "-",
          businessValueDetails: bv
            ? { name: bv.name, shortId: bv.shortId }
            : null,
          supportAssetsFormatted:
            bv && bv.supportAssets
              ? bv.supportAssets.map((sa) => sa.name || sa).join(", ")
              : (this.language === 'en' ? "None" : "Aucun"),
          a1Measures: a1Measures,
          a3Measures: a3Measures
            .filter((m) => {
              if (!m.dreadedEvents) return false;
              return (de.id && m.dreadedEvents.includes(de.id)) ||
                     (de._id && m.dreadedEvents.includes(de._id)) ||
                     (de.eventId && m.dreadedEvents.includes(de.eventId));
            })
            .map((m) => m.title || m.name || (this.language === 'en' ? "Unnamed measure" : "Mesure sans nom")),
          progressionRate: progressionRate,
          status:
            progressionRate >= 80
              ? "enriched"
              : progressionRate >= 40
                ? "partially-treated"
                : "not-treated",
        };
      });
    }

    if (events.length === 0) {
      this.pdf.addParagraph(
        w3T.noProgEvents,
        { style: "italic", color: "gray" },
      );
      return;
    }

    // Generate table
    const tableBody = events.map((event) => {
      let businessValue = event.businessValueDetails?.name || (this.language === 'en' ? "Undefined" : "Non défini");
      if (event.businessValueDetails?.shortId) {
        businessValue += ` [${event.businessValueDetails.shortId}]`;
      }

      let statusLabel = event.statusLabel || event.status || (this.language === 'en' ? "Undefined" : "Non défini");
      if (event.status === "enriched") statusLabel = this.language === 'en' ? "Enriched" : "Enrichi";
      else if (event.status === "partially-treated")
        statusLabel = this.language === 'en' ? "Partially Treated" : "Partiellement Traité";
      else if (event.status === "not-treated") statusLabel = this.language === 'en' ? "Not Treated" : "Non Traité";

      const a1Count = Array.isArray(event.a1Measures)
        ? event.a1Measures.length
        : 0;
      const a3Count = Array.isArray(event.a3Measures)
        ? event.a3Measures.length
        : 0;

      return [
        `${event.name}\n(${event.gravity} • ${event.securityPillar})`,
        businessValue,
        event.supportAssetsFormatted || (this.language === 'en' ? "None" : "Aucun"),
        a1Count.toString(),
        a3Count.toString(),
        `${event.progressionRate || 0}%`,
        statusLabel,
      ];
    });

    this.pdf.addTable(
      w3T.progressionHeaders,
      tableBody,
      {
        columnWidths: [40, 26, 43, 15, 15, 15, 15],
        fontSize: 8,
        headStyles: {
          fillColor: [30, 64, 175],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: "bold",
          halign: "center",
          valign: "middle",
        },
        styles: {
          fontSize: 8,
          cellPadding: 3,
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
          overflow: "linebreak",
          valign: "top",
        },
        columnStyles: {
          0: { fontStyle: "bold" },
          3: { halign: "center" },
          4: { halign: "center" },
          5: { halign: "center" },
          6: { halign: "center" },
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
      },
    );

    this.pdf.addSpace(3);
  }


  /**
   * Generate Atelier 4 - Threat Intelligence Section
   */
  async generateAtelier4ThreatIntelligence() {
    const w4T = this.T.w4;
    this.pdf.addSectionHeading(w4T.ctiTitle, 2);
    this.pdf.addParagraph(w4T.ctiDesc);
    this.pdf.addSpace(2);

    const ctiData = this.reportData.workshops?.workshop4?.ctiData || [];

    if (!ctiData || ctiData.length === 0) {
      this.pdf.addParagraph(
        w4T.noCtiData,
        { style: "italic", color: "gray" },
      );
      this.pdf.addSpace(3);
      return;
    }

    let totalAssets = 0;
    let totalCVEs = 0;
    let totalTechniques = 0;
    let criticalCVEs = 0;

    const assetThreatsBody = [];

    ctiData.forEach((result) => {
      const assets = result.assets || [];
      totalAssets += assets.length;
      totalCVEs += result.vulnerabilities?.length || 0;
      totalTechniques += result.techniques?.length || 0;

      const crit =
        result.vulnerabilities?.filter(
          (v) =>
            v.severity?.severity === "CRITICAL" || v.severity === "CRITICAL",
        ).length || 0;
      criticalCVEs += crit;

      const pathRef =
        result.attackPath?.referenceCode || result.attackPath?.id || "-";
      const riskScore = result.metadata?.overallRiskScore || "-";

      const topTechniques =
        result.techniques
          ?.slice(0, 3)
          .map((t) => t.name || t.techniqueName)
          .join(", ") + (result.techniques?.length > 3 ? "..." : "");

      const vulnsCount = result.vulnerabilities?.length || 0;
      const vulnsSummary = `${vulnsCount}`;

      assetThreatsBody.push([
        pathRef,
        assets
          .map((a) => a.name || a)
          .join(", ")
          .substring(0, 50) + (assets.length > 2 ? "..." : ""),
        result.attackPath?.sourceRiskName || "-",
        riskScore,
        vulnsSummary,
        topTechniques || (this.language === 'en' ? 'None' : 'Aucune'),
      ]);
    });

    const statsData = [
      [w4T.statsAssetsLabel, totalAssets.toString()],
      [w4T.statsCVELabel, totalCVEs.toString()],
      [w4T.statsCriticalLabel, criticalCVEs.toString()],
      [w4T.statsTechniquesLabel, totalTechniques.toString()],
    ];

    this.pdf.addTable(w4T.statsHeaders, statsData, {
      columnWidths: [80, 40],
      headStyles: { fillColor: [30, 64, 175] },
      tableWidth: 120,
      margin: { left: 15 },
    });
    this.pdf.addSpace(3);

    this.pdf.addSectionHeading(w4T.ctiSynthesisTitle, 3);
    this.pdf.addTable(
      w4T.ctiSynthesisHeaders,
      assetThreatsBody,
      {
        columnWidths: [15, 40, 40, 15, 15, 40],
        fontSize: 8,
        headStyles: { fillColor: [30, 64, 175] },
      },
    );
    this.pdf.addSpace(3);
  }

  /**
   * Generate Atelier 4 - Operational Scenarios Section
   */
  async generateAtelier4OperationalScenarios() {
    this.pdf.addSectionHeading("4.2 SC\u00c9NARIOS OP\u00c9RATIONNELS", 2);
    this.pdf.addParagraph(
      "Cette section d\u00e9taille les sc\u00e9narios op\u00e9rationnels construits.",
    );
    this.pdf.addSpace(2);

    const scenarios =
      this.reportData.workshops?.workshop4?.operationalScenarios || [];

    if (scenarios.length === 0) {
      this.pdf.addParagraph(
        "Aucun sc\u00e9nario op\u00e9rationnel identifi\u00e9.",
        { style: "italic", color: "gray" },
      );
      return;
    }

    // 1. Distributions (Severity & Likelihood)
    this.pdf.addSectionHeading("Analyse des Distributions", 3);

    // Calculate stats manually since reportData might not have pre-calculated distributions
    // Severity
    const severityCounts = { critical: 0, high: 0, medium: 0, low: 0 };
    const likelihoodCounts = { critical: 0, high: 0, medium: 0, low: 0 }; // Using same keys for simplicity, usually Likelihood is 1-4

    // Helper to map 1-4 to key
    const mapToKey = (val) => {
      const v = String(val).toLowerCase();
      if (
        v === "4" ||
        v.includes("critiqu") ||
        v.includes("tr\u00e8s") ||
        v.includes("certain")
      )
        return "critical";
      if (
        v === "3" ||
        v.includes("\u00e9lev\u00e9") ||
        v.includes("tr\u00e8s vrais")
      )
        return "high";
      if (v === "2" || v.includes("moyen") || v.includes("vraisemblable"))
        return "medium";
      if (v === "1" || v.includes("faible") || v.includes("peu")) return "low";
      return "low";
    };

    scenarios.forEach((s) => {
      severityCounts[mapToKey(s.severity)]++;
      likelihoodCounts[mapToKey(getScenarioVraisemblance(s))]++;
    });

    const total = scenarios.length;
    const fmtPct = (val) => `${((val / total) * 100).toFixed(0)}%`;

    // Create table bodies
    const severityBody = [
      [
        "Critique (4)",
        severityCounts.critical,
        fmtPct(severityCounts.critical),
      ],
      [
        "\u00c9lev\u00e9e (3)",
        severityCounts.high,
        fmtPct(severityCounts.high),
      ],
      ["Moyenne (2)", severityCounts.medium, fmtPct(severityCounts.medium)],
      ["Faible (1)", severityCounts.low, fmtPct(severityCounts.low)],
    ];

    const likelihoodBody = [
      [
        "Tr\u00e8s Vraisemblable (4)",
        likelihoodCounts.critical,
        fmtPct(likelihoodCounts.critical),
      ],
      [
        "Vraisemblable (3)",
        likelihoodCounts.high,
        fmtPct(likelihoodCounts.high),
      ],
      [
        "Possible (2)",
        likelihoodCounts.medium,
        fmtPct(likelihoodCounts.medium),
      ],
      [
        "Peu Vraisemblable (1)",
        likelihoodCounts.low,
        fmtPct(likelihoodCounts.low),
      ],
    ];

    // Ensure we fit on the page. Use sequential small tables.
    this.pdf.addParagraph("R\u00e9partition par Gravit\u00e9 :", {
      fontStyle: "bold",
    });
    this.pdf.addTable(["Niveau", "Nombre", "%"], severityBody, {
      tableWidth: 100,
      headStyles: { fillColor: [30, 64, 175] },
      margin: { left: 15 },
    });
    this.pdf.addSpace(2);

    this.pdf.addParagraph("R\u00e9partition par Vraisemblance :", {
      fontStyle: "bold",
    });
    this.pdf.addTable(["Niveau", "Nombre", "%"], likelihoodBody, {
      tableWidth: 100,
      headStyles: { fillColor: [30, 64, 175] },
      margin: { left: 15 },
    });
    this.pdf.addSpace(3);

    // 2. Scenarios Summary Table
    this.pdf.addSectionHeading(
      "Liste des Sc\u00e9narios Op\u00e9rationnels",
      3,
    );

    const scenariosBody = scenarios.map((s, idx) => {
      const ref = s.referenceCode || `S${String(s.scenarioNumber || idx + 1).padStart(2, "0")}`;
      const context = s.context || {};
      const source = context.sourceRisk || "-";
      const target = context.targetObjective || "-";
      const event = context.dreadedEvent || "-";

      return [
        ref,
        s.name || "Sans Nom",
        s.severity || "-",
        getScenarioVraisemblance(s) || "-",
        `Source: ${source}\nCible: ${target}\nER: ${event}`,
      ];
    });

    this.pdf.addTable(
      [
        "R\u00e9f",
        "Nom du Sc\u00e9nario",
        "Gravit\u00e9",
        "Vrais.",
        "Contexte (Source / Cible / ER)",
      ],
      scenariosBody,
      {
        columnWidths: [15, 35, 15, 15, 90],
        fontSize: 8,
        headStyles: { fillColor: [30, 64, 175] },
        didParseCell: (data) => {
          // Colorize Severity/Likelihood cells
          if (
            data.section === "body" &&
            (data.column.index === 2 || data.column.index === 3)
          ) {
            const val = String(data.cell.raw).toLowerCase();
            let color = null;
            if (val === "4" || val.includes("critique"))
              color = [220, 38, 38]; // Red
            else if (val === "3" || val.includes("\u00e9lev\u00e9"))
              color = [234, 88, 12]; // Orange
            else if (val === "2" || val.includes("moyen"))
              color = [234, 179, 8]; // Yellow
            else if (val === "1" || val.includes("faible"))
              color = [34, 197, 94]; // Green

            if (color) {
              data.cell.styles.fillColor = color;
              data.cell.styles.textColor = [255, 255, 255];
              data.cell.styles.fontStyle = "bold";
            }
          }
        },
      },
    );
  }

  /**
   * Generate Atelier 4 - Operational Scenarios Section (Detailed)
   * Uses the specialized OperationalScenariosPdfGenerator
   */
  async generateAtelier4OperationalScenariosDetailed() {
    const w4T = this.T.w4;
    this.pdf.addSectionHeading(w4T.opScenariosTitle, 2);
    this.pdf.addParagraph(w4T.opScenariosDesc);
    this.pdf.addSpace(2);

    const scenarios =
      this.reportData.workshops?.workshop4?.operationalScenarios || [];

    if (scenarios.length === 0) {
      this.pdf.addParagraph(
        w4T.noOpScenarios,
        { style: "italic", color: "gray" },
      );
      return;
    }

    try {
      const mod = await import("./operationalScenariosPdfGenerator");
      const OperationalScenariosPdfGenerator = mod.default;

      const opGen = new OperationalScenariosPdfGenerator(
        {
          scenarios: scenarios,
          summary: { totalScenarios: scenarios.length },
          severityDistribution: {},
          likelihoodDistribution: {},
        },
        { companyName: this.reportData.metadata?.companyName },
        this.pdf.pdf,
        this.language,
      );

      const severityCounts = { critical: 0, high: 0, medium: 0, low: 0 };
      const likelihoodCounts = { critical: 0, high: 0, medium: 0, low: 0 };
      const mapToKey = (val) => {
        const v = String(val).toLowerCase();
        if (v === "4" || v.includes("critiqu")) return "critical";
        if (v === "3" || v.includes("\u00e9lev\u00e9") || v.includes("elev")) return "high";
        if (v === "2" || v.includes("moyen") || v.includes("medium")) return "medium";
        return "low";
      };
      scenarios.forEach((s) => {
        severityCounts[mapToKey(s.severity)]++;
        likelihoodCounts[mapToKey(getScenarioVraisemblance(s))]++;
      });
      opGen.reportData.severityDistribution = severityCounts;
      opGen.reportData.likelihoodDistribution = likelihoodCounts;

      opGen.generateScenariosDetails(false, this.pdf.currentY, true);

      this.pdf.pageNumber = this.pdf.pdf.internal.getNumberOfPages();
      this.pdf.currentY = this.pdf.pageHeight;
    } catch (e) {
      console.error("Error in detailed generation:", e);
      this.pdf.addParagraph(
        this.language === 'en'
          ? "Error during detailed generation. Falling back to simple list."
          : "Erreur lors de la g\u00e9n\u00e9ration d\u00e9taill\u00e9e. Repli sur liste simple.",
      );
      scenarios.forEach((s) =>
        this.pdf.addParagraph(`- ${s.name} (${s.severity})`),
      );
    }
  }

  /**
   * Helper to render visual Risk Matrix Grid (Initial or Residual)
   */
  generatePdfRiskMatrixGrid(sectionTitle, riskScenarios, riskScenarioIndexMap, isResidual = false) {
    this.pdf.addSectionHeading(sectionTitle, 2);
    this.pdf.addParagraph(
      isResidual
        ? this.T.w5.matrixResidualDesc
        : this.T.w5.matrixInitialDesc
    );
    this.pdf.addSpace(3);

    const doc = this.pdf.pdf;
    const matrixLevels = this.reportData.riskLevels || [];
    const matrixCount = this.reportData.riskLevelsCount || 5;

    // Check page space before starting matrix (matrix height ~110mm)
    this.pdf.checkPageBreak(120);

    const startX = this.pdf.margin + 20;
    const startY = this.pdf.currentY;
    const cellWidth = 30;
    const cellHeight = 15;

    const matrixColors = {
      green: [220, 252, 231],
      greenText: [22, 101, 52],
      yellow: [254, 240, 138],
      yellowText: [133, 77, 14],
      orange: [255, 237, 213],
      orangeText: [154, 52, 18],
      red: [254, 202, 202],
      redText: [153, 27, 27],
    };

    const getCellColor = (gravity, likelihood) => {
      const score = gravity * likelihood;
      if (matrixLevels.length > 0) {
        const lv = matrixLevels.find(
          (l) => score >= l.minScore && score <= l.maxScore
        );
        const colorKey = lv?.color || (score >= 12 ? 'red' : score >= 8 ? 'orange' : score >= 4 ? 'yellow' : 'green');
        return {
          fill: matrixColors[colorKey] || matrixColors.green,
          text: matrixColors[`${colorKey}Text`] || matrixColors.greenText,
          label: lv?.label || `Niveau ${score}`,
        };
      }
      if (score >= 12) return { fill: matrixColors.red, text: matrixColors.redText, label: "Risque Critique" };
      if (score >= 8) return { fill: matrixColors.orange, text: matrixColors.orangeText, label: "Risque Majeur" };
      if (score >= 4) return { fill: matrixColors.yellow, text: matrixColors.yellowText, label: "Risque Moyen" };
      return { fill: matrixColors.green, text: matrixColors.greenText, label: "Risque Mineur" };
    };

    // Column Headers V1..V4
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(71, 85, 105);
    for (let c = 0; c < 4; c++) {
      const x = startX + c * cellWidth;
      doc.text(`V${c + 1}`, x + cellWidth / 2, startY + 4, { align: "center" });
    }

    const gridStartY = startY + 6;

    // Deduplicate risk scenarios by attackPathId
    const uniqueScenarios = [];
    const seenAp = new Set();
    for (const rs of riskScenarios) {
      const apId = rs.attackPathId || rs.id;
      if (apId && !seenAp.has(apId)) {
        seenAp.add(apId);
        uniqueScenarios.push(rs);
      }
    }

    for (let r = 0; r < matrixCount; r++) {
      const y = gridStartY + r * cellHeight;
      const gravity = matrixCount - r;

      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(51, 65, 85);
      doc.text(`G${gravity}`, startX - 6, y + cellHeight / 2 + 2, { align: "right" });

      for (let c = 0; c < 4; c++) {
        const likelihood = c + 1;
        const x = startX + c * cellWidth;
        const colorStyle = getCellColor(gravity, likelihood);

        doc.setFillColor(...colorStyle.fill);
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.4);
        doc.rect(x, y, cellWidth, cellHeight, "FD");

        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(148, 163, 184);
        doc.text(String(gravity * likelihood), x + cellWidth - 2, y + 4.5, { align: "right" });

        const cellScenarios = uniqueScenarios.filter((rs) => {
          const rr = rs.residualRisk || {};
          const g = isResidual ? (Number(rr.residualGravity) || rs.gravity) : rs.gravity;
          const l = isResidual ? (Number(rr.residualLikelihood) || rs.likelihood) : rs.likelihood;
          return Number(g) === gravity && Number(l) === likelihood;
        });

        if (cellScenarios.length > 0) {
          const shortRefs = cellScenarios.map((s) => {
            const apRef = riskScenarioIndexMap[s.attackPathId];
            return (apRef ? apRef.shortRef : s.ref) + (s.preliminary ? '*' : '');
          }).join(', ');

          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...colorStyle.text);
          const lines = doc.splitTextToSize(shortRefs, cellWidth - 3);
          const lineY = y + 6 + (cellHeight - 6 - lines.length * 3.5) / 2;
          lines.forEach((line, idx) => {
            doc.text(line, x + cellWidth / 2, lineY + idx * 3.5, { align: "center" });
          });
        }
      }
    }

    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(100, 116, 139);
    doc.text(
      this.T.w5.matrixAxisGravity,
      startX - 16,
      gridStartY + (matrixCount * cellHeight) / 2,
      { angle: 90, align: "center" }
    );
    doc.text(
      this.T.w5.matrixAxisLikelihood,
      startX + (4 * cellWidth) / 2,
      gridStartY + matrixCount * cellHeight + 6,
      { align: "center" }
    );

    // Legend
    const legendX = startX + 4 * cellWidth + 8;
    let legendY = gridStartY;
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 65, 85);
    doc.text(this.T.w5.matrixLegendLabel, legendX, legendY);
    legendY += 5;

    const legendItems = matrixLevels.length > 0
      ? matrixLevels.map(lv => ({
          label: lv.label,
          color: matrixColors[lv.color] || matrixColors.green,
        }))
      : [
          { label: this.T.w5.matrixLevelCritical, color: matrixColors.red },
          { label: this.T.w5.matrixLevelMajor, color: matrixColors.orange },
          { label: this.T.w5.matrixLevelModerate, color: matrixColors.yellow },
          { label: this.T.w5.matrixLevelMinor, color: matrixColors.green },
        ];

    legendItems.forEach((item) => {
      doc.setFillColor(...item.color);
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.2);
      doc.rect(legendX, legendY, 5, 4, "FD");

      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(51, 65, 85);
      doc.text(item.label, legendX + 7, legendY + 3.2);
      legendY += 5.5;
    });

    this.pdf.currentY = gridStartY + matrixCount * cellHeight + 12;
    this.pdf.addSpace(3);
  }

  /**
   * Helper to render Registre de tra\u00e7abilit\u00e9 \u00c9v\u00e9nements redout\u00e9s vs sc\u00e9narios de risque
   */
  generatePdfCoverageRegistry(riskScenarios, riskScenarioIndexMap) {
    const w5T = this.T.w5;
    this.pdf.addSectionHeading(w5T.coverageRegistryTitle, 2);
    this.pdf.addParagraph(w5T.coverageRegistryDesc);
    this.pdf.addSpace(3);

    const workshop1 = this.reportData.workshops?.workshop1 || {};
    const dreadedEvents = workshop1.dreadedEvents || [];
    const businessValues = workshop1.businessValues || [];
    const strategicScenarios = this.reportData.workshops?.workshop5?.strategicScenarios || this.reportData.workshops?.workshop3?.strategicScenarios || [];
    const operationalScenarios = this.reportData.workshops?.workshop4?.operationalScenarios || [];

    const bvNameById = {};
    for (const bv of businessValues) {
      if (bv.id) bvNameById[bv.id] = bv.name || null;
    }

    const { rows } = buildCoverageRegistry({
      dreadedEvents,
      strategicScenarios,
      operationalScenarios,
      riskScenarioIndexMap,
      bvNameById,
    });

    if (rows.length === 0) {
      this.pdf.addParagraph(w5T.noCoverageEvents);
      this.pdf.addSpace(4);
      return;
    }

    const uncovered = rows.filter(r => !r.isCovered);
    if (uncovered.length > 0) {
      this.pdf.addParagraph(
        this.language === 'en'
          ? `Warning: ${uncovered.length} feared event(s) from Workshop 1 are not linked to any risk scenario.`
          : `Attention : ${uncovered.length} \u00e9v\u00e9nement(s) redout\u00e9(s) de l'Atelier 1 ne sont rattach\u00e9s \u00e0 aucun sc\u00e9nario de risque.`
      );
      this.pdf.addSpace(2);
    }

    const severityLabels = this.language === 'en' ? {
      minor: 'Minor',
      moderate: 'Moderate',
      major: 'Major',
      critical: 'Critical',
      catastrophic: 'Catastrophic',
    } : {
      minor: 'Mineure',
      moderate: 'Mod\u00e9r\u00e9e',
      major: 'Majeure',
      critical: 'Critique',
      catastrophic: 'Catastrophique',
    };

    const headers = w5T.coverageRegistryHeaders;

    const tableRows = rows.map(r => [
      r.name || '\u2014',
      severityLabels[r.severity] || this.formatSeverity(r.severity) || '\u2014',
      r.bvName || '\u2014',
      r.isCovered ? r.scenarioRefs.join(', ') : (this.language === 'en' ? 'Not linked' : 'Non rattach\u00e9'),
      r.coverageJustification || '\u2014',
    ]);

    this.pdf.addTable(headers, tableRows, {
      columnWidths: [45, 20, 35, 30, 53],
      headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontSize: 7, fontStyle: "bold" },
      styles: { fontSize: 7, cellPadding: 2, overflow: "linebreak" },
      didParseCell: (data) => {
        if (data.section === "body") {
          const rowData = rows[data.row.index];
          if (rowData && !rowData.isCovered) {
            if (data.column.index === 3) {
              data.cell.styles.textColor = [220, 38, 38];
              data.cell.styles.fontStyle = "bold";
            }
          }
        }
      },
      margin: { left: 10, right: 10 },
      pageBreak: "auto",
    });

    this.pdf.addSpace(5);
  }

  /**
   * 5. ATELIER 5 : TRAITEMENT DU RISQUE ET SUIVI — Vue Globale, Traitement et Suivi
   */
  async generateWorkshop5Section() {
    const workshop5Data = this.reportData.workshops?.workshop5 || {};
    const strategicScenarios = workshop5Data.strategicScenarios || [];

    if (strategicScenarios.length === 0) {
      this.pdf.addParagraph(
        "Aucune donn\u00e9e de traitement de risque disponible.",
      );
      return;
    }

    // Compute risk scenarios: flat list of strategic × operational pairs
    const operationalScenarios = this.reportData.workshops?.workshop4?.operationalScenarios || [];
    const riskScenarios = operationalScenarios.reduce((acc, op) => {
      const ss = strategicScenarios.find(s =>
        (s.attackPaths || []).some(ap => {
          const apId = typeof ap === 'string' ? ap : ap.id;
          return apId === op.attackPathId;
        })
      );
      if (!ss) return acc;

      const apId = op.attackPathId;
      const treatment = ss.attackPathTreatments?.[apId] || {};
      const rawRR = ss.attackPathResidualRisks;
      const rr = rawRR?.[apId] || (rawRR?.description || rawRR?.vulnerabilities || rawRR?.aggravatingFactors || rawRR?.monitoring ? rawRR : {});
      const gravity = ss.gravity || 1;
      const likelihood = Number(op.scenarioVraisemblance || op.vraisemblance || 1);

      const measures = [
        ...(ss.customMeasures || []).map(m => ({ ...m, tag: 'Perso' })),
        ...(ss.dreadedEventMeasures || []).map(m => ({ ...m, tag: 'A1' })),
        ...(ss.ecosystemMeasures || []).map(m => ({ ...m, tag: 'A3' })),
      ].filter(m => {
        const ids = [];
        if (m.attackPaths?.length) m.attackPaths.forEach(a => ids.push(typeof a === 'string' ? a : a));
        if (m.attackPathId) ids.push(m.attackPathId);
        return ids.length === 0 || ids.some(id => id === apId);
      });

      acc.push({
        id: `RS-${ss.id}-${apId}-${op.id}`,
        strategicScenarioId: ss.id,
        attackPathId: apId,
        operationalScenarioId: op.id,
        name: `${ss.name || ''} \u2192 ${op.name || ''}`,
        ref: ss.referenceCode || ss.scenarioNumber?.toString() || ss.id?.slice(-4) || '',
        gravity,
        likelihood,
        riskLevel: gravity * likelihood,
        treatment,
        residualRisk: rr,
        measures,
        scenarioName: ss.name || '',
        attackPathName: typeof ss.attackPaths?.find(ap => (typeof ap === 'string' ? ap : ap.id) === apId) === 'object'
          ? ss.attackPaths.find(ap => (typeof ap === 'string' ? ap : ap.id) === apId).name || ''
          : '',
        operationalScenarioName: op.name || '',
      });
      return acc;
    }, []);

    // Second pass: preliminary attack paths (no operational scenario, manual likelihood)
    const coveredPathIds = new Set(riskScenarios.map((rs) => rs.attackPathId));
    for (const ss of strategicScenarios) {
      for (const ap of (ss.attackPaths || [])) {
        const apId = typeof ap === 'string' ? ap : ap.id;
        if (!apId || coveredPathIds.has(apId)) continue;
        const preliminary = (typeof ap === 'object' && ap.preliminary?.active) ? ap.preliminary : null;
        if (!preliminary) continue;

        const likelihood = Number(preliminary.likelihood) || 1;
        const gravity = ss.gravity || 1;
        const rr = ss.attackPathResidualRisks?.[apId] || {};

        const measures = [
          ...(ss.customMeasures || []).map((m) => ({ ...m, tag: 'Perso' })),
          ...(ss.dreadedEventMeasures || []).map((m) => ({ ...m, tag: 'A1' })),
          ...(ss.ecosystemMeasures || []).map((m) => ({ ...m, tag: 'A3' })),
        ].filter((m) => {
          const ids = [];
          if (m.attackPaths?.length) m.attackPaths.forEach((a) => ids.push(typeof a === 'string' ? a : a));
          if (m.attackPathId) ids.push(m.attackPathId);
          return ids.length === 0 || ids.some((id) => id === apId);
        });

        riskScenarios.push({
          id: `RS-${ss.id}-${apId}-preliminary`,
          strategicScenarioId: ss.id,
          attackPathId: apId,
          operationalScenarioId: null,
          name: `${ss.name || ''} \u2192 ${(typeof ap === 'object' && ap.name) || ''} (\u00c9tude pr\u00e9liminaire)`,
          ref: ss.referenceCode || ss.scenarioNumber?.toString() || ss.id?.slice(-4) || '',
          gravity,
          likelihood,
          riskLevel: gravity * likelihood,
          treatment: ss.attackPathTreatments?.[apId] || {},
          residualRisk: rr,
          measures,
          scenarioName: ss.name || '',
          attackPathName: (typeof ap === 'object' && ap.name) || '',
          operationalScenarioName: null,
          preliminary: true,
          preliminaryJustification: preliminary.justification || '',
        });
      }
    }

    if (riskScenarios.length === 0) {
      this.pdf.addParagraph("Aucun sc\u00e9nario de risque disponible.");
      return;
    }

    // Build riskScenarioIndexMap matching UI (R1, R2...)
    const riskScenarioIndexMap = {};
    let idx = 0;
    for (const ss of strategicScenarios) {
      const scenarioRef = ss.referenceCode || ss.id;
      for (const ap of (ss.attackPaths || [])) {
        const id = typeof ap === 'string' ? ap : ap.id;
        if (id && !riskScenarioIndexMap[id]) {
          riskScenarioIndexMap[id] = {
            shortRef: `R${++idx}`,
            originalRef: typeof ap === 'string' ? ap : (ap.referenceCode || ap.name || ap.id),
            name: typeof ap === 'string' ? '' : (ap.name || ''),
            scenarioRef,
          };
        }
      }
    }

    // 5.1 Initial risk matrix
    this.generatePdfRiskMatrixGrid(this.T.w5.initialMatrixTitle, riskScenarios, riskScenarioIndexMap, false);

    // 5.2 Residual risk matrix
    this.generatePdfRiskMatrixGrid(this.T.w5.residualMatrixTitle, riskScenarios, riskScenarioIndexMap, true);


    // Collect unique measures with displayAttackPathRef
    const allMeasures = [];
    const seen = new Set();
    for (const rs of riskScenarios) {
      for (const m of (rs.measures || [])) {
        if (!seen.has(m.id)) {
          seen.add(m.id);

          // Compute displayAttackPathRef matching UI
          const ids = new Set();
          if (m.attackPathId) ids.add(m.attackPathId);
          if (m.attackPaths?.length) {
            for (const a of m.attackPaths) {
              ids.add(typeof a === 'string' ? a : (a.id || a));
            }
          }
          const refs = [];
          for (const id of ids) {
            if (riskScenarioIndexMap[id]) refs.push(riskScenarioIndexMap[id].shortRef);
          }

          allMeasures.push({
            ...m,
            displayAttackPathRef: refs.length ? refs.join(', ') : null,
          });
        }
      }
    }

    if (allMeasures.length === 0) {
      this.pdf.addParagraph("Aucune mesure de s\u00e9curit\u00e9 r\u00e9pertori\u00e9e.");
      this.pdf.addSpace(5);
      return;
    }

    // Group by category (matching UI)
    const alias = { governance: 'gouvernance et anticipation', defense: 'd\u00e9fense', resilience: 'r\u00e9silience' };
    const catLabel = this.language === 'en' ? {
      'gouvernance et anticipation': 'Governance',
      protection: 'Protection',
      'd\u00e9fense': 'Defense',
      'r\u00e9silience': 'Resilience',
      other: 'Other',
    } : {
      'gouvernance et anticipation': 'Gouvernance',
      protection: 'Protection',
      'd\u00e9fense': 'D\u00e9fense',
      'r\u00e9silience': 'R\u00e9silience',
      other: 'Autre',
    };
    const catColor = {
      'gouvernance et anticipation': [109, 40, 217],
      protection: [29, 78, 216],
      'd\u00e9fense': [21, 128, 61],
      'r\u00e9silience': [234, 88, 12],
      other: [55, 65, 81],
    };

    const grouped = {};
    for (const m of allMeasures) {
      const c = alias[m.category] || m.category || 'other';
      if (!grouped[c]) grouped[c] = [];
      grouped[c].push(m);
    }

    const catOrder = ['gouvernance et anticipation', 'protection', 'd\u00e9fense', 'r\u00e9silience', 'other'];
    const sortedCats = Object.keys(grouped).sort(
      (a, b) => (catOrder.indexOf(a) >= 0 ? catOrder.indexOf(a) : 99) - (catOrder.indexOf(b) >= 0 ? catOrder.indexOf(b) : 99),
    );

    const statusLabel = this.language === 'en' ? {
      implemented: 'Done',
      in_progress: 'In progress',
      planned: 'Planned',
    } : {
      implemented: 'Fait',
      in_progress: 'En cours',
      planned: 'Planifi\u00e9',
    };
    const statusColor = {
      implemented: [34, 197, 94],
      in_progress: [234, 179, 8],
      planned: [59, 130, 246],
    };

    this.pdf.addSectionHeading(this.T.w5.measuresTitle, 2);
    this.pdf.addParagraph(this.T.w5.measuresDesc);
    this.pdf.addSpace(2);

    const pMap = ['', 'low', 'medium', 'high', 'critical'];
    const pLabels = ['\u2014', 'P4', 'P3', 'P2', 'P1'];
    const costLabels = ['\u2014', '+', '++', '+++'];

    const headers = this.T.w5.measuresHeaders;

    const tableRows = [];
    for (const cat of sortedCats) {
      const measures = grouped[cat];
      const color = catColor[cat] || [55, 65, 81];

      // Category header row spanning all columns
      tableRows.push([
        {
          content: catLabel[cat] || cat,
          colSpan: 10,
          styles: { fillColor: color, textColor: [255, 255, 255], fontStyle: "bold", halign: "center", fontSize: 7 },
        },
      ]);

      for (const m of measures) {
        const stat = m.implementation || m.decision;
        const pIdx = pMap.indexOf(m.priority || '');
        const costIdx = Math.min(3, Math.max(0, Number(m.cost || 0)));
        tableRows.push([
          m.name || m.title || m.measureName || m.description || "\u2014",
          m.displayAttackPathRef || "\u2014",
          m.responsiblePerson || m.responsibleParty || "\u2014",
          m.obstacles || m.freins || "\u2014",
          costLabels[costIdx],
          Number(m.budget) > 0 ? `${Number(m.budget)} \u20ac` : "\u2014",
          m.estimatedEffort || m.effort || m.charge || "\u2014",
          m.deadline || m.echeance || "\u2014",
          pLabels[pIdx >= 0 ? pIdx : 0],
          {
            content: statusLabel[stat] || stat || "\u2014",
            styles: {
              fillColor: statusColor[stat] || [148, 163, 184],
              textColor: [255, 255, 255],
              fontStyle: "bold",
              halign: "center",
            },
          },
        ]);
      }
    }

    this.pdf.addTable(headers, tableRows, {
      columnWidths: [40, 18, 19, 20, 14, 16, 14, 14, 12, 16],
      headStyles: { fillColor: [41, 82, 163], fontSize: 6 },
      styles: { fontSize: 6, cellPadding: 1.5, overflow: "linebreak" },
      margin: { left: 10, right: 10 },
      tableWidth: 183,
      pageBreak: "auto",
    });

    this.pdf.addSpace(5);

    // ── Implementation schedule ──
    this.pdf.addSectionHeading(this.T.w5.scheduleTitle, 2);
    this.pdf.addParagraph(this.T.w5.scheduleDesc);
    this.pdf.addSpace(2);

    const now = new Date();
    const msMonth = 30.44 * 24 * 60 * 60 * 1000;

    const quarterKey = (deadline) => {
      if (!deadline) return 'none';
      const d = new Date(deadline + 'T00:00:00');
      if (isNaN(d.getTime())) return 'none';
      const months = (d - now) / msMonth;
      if (months <= 3) return 't0';
      if (months <= 6) return 't3';
      if (months <= 9) return 't6';
      if (months <= 12) return 't9';
      return 't12';
    };

    const qLabel = this.language === 'en' ? {
      t0: 'T+0 (0 to 3 months)',
      t3: 'T+3 (3 to 6 months)',
      t6: 'T+6 (6 to 9 months)',
      t9: 'T+9 (9 to 12 months)',
      t12: 'T+12 (12+ months)',
      none: 'Unplanned',
    } : {
      t0: 'T+0 (0 \u00e0 3 mois)',
      t3: 'T+3 (3 \u00e0 6 mois)',
      t6: 'T+6 (6 \u00e0 9 mois)',
      t9: 'T+9 (9 \u00e0 12 mois)',
      t12: 'T+12 (12 mois et +)',
      none: 'Non planifi\u00e9',
    };

    const qColor = {
      t0: [30, 64, 175],
      t3: [37, 99, 235],
      t6: [59, 130, 246],
      t9: [96, 165, 250],
      t12: [147, 197, 253],
      none: [100, 116, 139],
    };

    const qOrder = ['t0', 't3', 't6', 't9', 't12', 'none'];

    const groupedByQ = {};
    for (const m of allMeasures) {
      const q = quarterKey(m.deadline);
      if (!groupedByQ[q]) groupedByQ[q] = [];
      groupedByQ[q].push(m);
    }

    const qHeaders = this.T.w5.scheduleHeaders;
    const qRows = [];

    for (const q of qOrder) {
      const measures = groupedByQ[q];
      if (!measures?.length) continue;
      const color = qColor[q] || [100, 116, 139];

      qRows.push([{
        content: qLabel[q] || q,
        colSpan: 6,
        styles: { fillColor: color, textColor: [255, 255, 255], fontStyle: "bold", halign: "center", fontSize: 7 },
      }]);

      for (const m of measures) {
        const stat = m.implementation || m.decision;
        qRows.push([
          m.name || m.title || m.measureName || m.description || "\u2014",
          m.displayAttackPathRef || "\u2014",
          m.responsiblePerson || m.responsibleParty || "\u2014",
          m.deadline || m.echeance || "\u2014",
          m.estimatedEffort || m.effort || m.charge || "\u2014",
          {
            content: statusLabel[stat] || stat || "\u2014",
            styles: {
              fillColor: statusColor[stat] || [148, 163, 184],
              textColor: [255, 255, 255],
              fontStyle: "bold",
              halign: "center",
            },
          },
        ]);
      }
    }

    if (qRows.length > 0) {
      this.pdf.addTable(qHeaders, qRows, {
        columnWidths: [50, 25, 32, 22, 18, 36],
        headStyles: { fillColor: [41, 82, 163], fontSize: 7 },
        styles: { fontSize: 7, cellPadding: 1.5, overflow: "linebreak" },
        margin: { left: 10, right: 10 },
        tableWidth: 183,
        pageBreak: "auto",
      });
    } else {
      this.pdf.addParagraph(this.language === 'en' ? 'No measures with a specified deadline.' : 'Aucune mesure avec \u00e9ch\u00e9ance renseign\u00e9e.');
    }

    this.pdf.addSpace(5);

    // ── Risk treatment ──
    this.pdf.addSectionHeading(this.T.w5.treatmentTitle, 2);
    this.pdf.addParagraph(this.T.w5.treatmentDesc);
    this.pdf.addSpace(3);

    const treatOptLabel = this.language === 'en' ? {
      reduce: 'Reduction',
      accept: 'Acceptance',
      transfer: 'Transfer',
      avoid: 'Avoidance',
    } : {
      reduce: 'Reduction',
      accept: 'Acceptation',
      transfer: 'Transfert',
      avoid: 'Evitement',
    };
    const treatOptColor = {
      reduce: [59, 130, 246],
      accept: [234, 179, 8],
      transfer: [168, 85, 247],
      avoid: [220, 38, 38],
    };

    const seenAp = new Set();
    const tHeaders = this.T.w5.treatmentHeaders;
    const tRows = [];

    for (const rs of riskScenarios) {
      if (seenAp.has(rs.attackPathId)) continue;
      seenAp.add(rs.attackPathId);

      const apRef = riskScenarioIndexMap[rs.attackPathId];
      const shortRef = (apRef ? apRef.shortRef : rs.ref) + (rs.preliminary ? '*' : '');
      const t = rs.treatment || {};
      const rr = rs.residualRisk || {};
      const rrg = Number(rr.residualGravity) || rs.gravity;
      const rrl = Number(rr.residualLikelihood) || rs.likelihood;
      const rrScore = rrg * rrl;

      tRows.push([
        shortRef || '-',
        rs.attackPathName || '-',
        {
          content: treatOptLabel[t.option] || t.option || '-',
          styles: { fillColor: treatOptColor[t.option] || [148, 163, 184], textColor: [255, 255, 255], fontStyle: "bold", halign: "center", fontSize: 6 },
        },
        t.responsiblePerson || '-',
        t.justification || '-',
        rrScore.toString(),
      ]);
    }

    if (tRows.length > 0) {
      this.pdf.addTable(tHeaders, tRows, {
        columnWidths: [14, 38, 18, 18, 49, 15],
        headStyles: { fillColor: [41, 82, 163], fontSize: 7 },
        styles: { fontSize: 7, cellPadding: 1.5, overflow: "linebreak" },
        margin: { left: 10, right: 10 },
        pageBreak: "auto",
      });
      this.pdf.addSpace(4);
    }

    // ── Residual risk analysis ──
    this.pdf.addSectionHeading(this.T.w5.residualAnalysisTitle, 3);
    this.pdf.addSpace(2);

    const seenApRr = new Set();
    const rrHeaders = this.T.w5.residualAnalysisHeaders;
    const rrRows = [];

    for (const rs of riskScenarios) {
      if (seenApRr.has(rs.attackPathId)) continue;
      seenApRr.add(rs.attackPathId);

      const apRef = riskScenarioIndexMap[rs.attackPathId];
      const shortRef = (apRef ? apRef.shortRef : rs.ref) + (rs.preliminary ? '*' : '');
      const rr = rs.residualRisk || {};

      if (rr.description || rr.vulnerabilities || rr.aggravatingFactors || rr.monitoring) {
        rrRows.push([
          shortRef || '-',
          rr.description || '-',
          rr.vulnerabilities || '-',
          rr.aggravatingFactors || '-',
          rr.monitoring || '-',
        ]);
      }
    }

    if (rrRows.length > 0) {
      this.pdf.addTable(rrHeaders, rrRows, {
        columnWidths: [12, 44, 38, 38, 38],
        headStyles: { fillColor: [30, 64, 175], fontSize: 7 },
        styles: { fontSize: 7, cellPadding: 1.5, overflow: "linebreak" },
        margin: { left: 10, right: 10 },
        pageBreak: "auto",
      });
    } else {
      this.pdf.addParagraph(this.language === 'en' ? 'No residual risk data entered.' : 'Aucune donnee de risque residuel renseignee.');
    }

    this.pdf.addSpace(5);

    // Registre de traçabilité Événements redoutés vs scénarios de risque
    this.generatePdfCoverageRegistry(riskScenarios, riskScenarioIndexMap);
  }

  async generateAnnexes() {
    const annT = this.T.annexes;
    this.pdf.addSectionHeading(annT.title, 1);
    this.pdf.addParagraph(annT.desc);
    this.pdf.addSpace(5);

    // 1. Gravity scale
    this.pdf.addSectionHeading(annT.gravityScaleTitle, 2);
    this.pdf.addParagraph(annT.gravityScaleDesc);
    this.pdf.addSpace(2);

    const severityCount = this.reportData.riskLevelsCount || 5;

    const allSeverityLabels = this.language === 'en'
      ? ['Catastrophic', 'Critical', 'Major', 'Moderate', 'Minor']
      : ['Catastrophique', 'Critique', 'Majeure', 'Mod\u00e9r\u00e9e', 'Mineure'];
    const severityLabels = allSeverityLabels.slice(-severityCount);

    const severityColorMap = {
      0: { bg: [17, 24, 39], text: [255, 255, 255] },
      1: { bg: [254, 226, 226], text: [185, 28, 28] },
      2: { bg: [255, 237, 213], text: [194, 65, 12] },
      3: { bg: [254, 252, 232], text: [161, 98, 7] },
      4: { bg: [209, 250, 229], text: [6, 95, 70] },
    };

    const savedGravity = (this.reportData && this.reportData.gravityLevels) || [];
    const gravityRows = Array.from({ length: severityCount }, (_, i) => {
      const level = severityCount - i;
      const saved = savedGravity.find(x => Number(x.level) === level);
      const g = getGravityByLevel(level) || {};
      const label = (saved && saved.label) || severityLabels[i];
      return [`${(saved && saved.code) || g.code || `G${level}`} - ${label}`, (saved && saved.description) || g.description || 'Description non disponible', ''];
    });

    this.pdf.addTable(annT.gravityScaleHeaders, gravityRows, {
      columnWidths: [40, 45, 90],
      headStyles: { fillColor: [41, 82, 163] },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 0) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.halign = "center";
          data.cell.styles.valign = "middle";
          const fromEnd = severityCount - 1 - data.row.index;
          const colors = severityColorMap[fromEnd] || severityColorMap[4];
          data.cell.styles.fillColor = colors.bg;
          data.cell.styles.textColor = colors.text;
        }
      },
    });
    this.pdf.addSpace(5);

    // 2. SR and OV categories
    this.pdf.addSectionHeading(annT.srOvTitle, 2);

    this.pdf.addSectionHeading(annT.srTitle, 3);
    const srCategories = annT.srTable;
    this.pdf.addTable(srCategories[0], srCategories.slice(1), {
      columnWidths: [50, 120],
    });
    this.pdf.addSpace(3);

    this.pdf.addSectionHeading(annT.ovTitle, 3);
    const ovCategories = [
      ["Objectif", "Description"],
      ...annT.ovTable.slice(1),
    ];
    this.pdf.addTable(annT.ovTable[0], ovCategories, {
      columnWidths: [50, 120],
    });
    this.pdf.addSpace(5);

    // 3. SR/OV evaluation scale
    this.pdf.addSectionHeading(annT.srovScaleTitle, 2);
    this.pdf.addParagraph(annT.srovScaleDesc);
    this.pdf.addSpace(2);

    const srovScale = annT.srovScaleTable;
    this.pdf.addTable(srovScale[0], srovScale.slice(1), {
      columnWidths: [20, 30, 120],
    });
    this.pdf.addSpace(5);

    // 4. Stakeholder evaluation scale
    this.pdf.addSectionHeading(annT.stakeholderScaleTitle, 2);
    this.pdf.addParagraph(annT.stakeholderScaleDesc);
    this.pdf.addSpace(2);

    const ecosystemScale = [
      annT.stakeholderScaleHeaders,
      ...annT.stakeholderScaleRows,
    ];
    this.pdf.addTable(ecosystemScale[0], ecosystemScale.slice(1), {
      columnWidths: [30, 30, 110],
    });
    this.pdf.addSpace(5);

    // 5. Likelihood scales
    this.pdf.addSectionHeading(annT.likelihoodScalesTitle, 2);
    this.pdf.addParagraph(annT.likelihoodScalesDesc);
    this.pdf.addSpace(2);

    this.pdf.addSectionHeading(annT.likelihoodExpressTitle, 3);
    this.pdf.addParagraph(annT.likelihoodExpressDesc);

    this.pdf.addSectionHeading(annT.likelihoodStandardTitle, 3);
    this.pdf.addParagraph(annT.likelihoodStandardDesc);

    this.pdf.addSectionHeading(annT.likelihoodAdvancedTitle, 3);
    this.pdf.addParagraph(annT.likelihoodAdvancedDesc);
    this.pdf.addSpace(5);

    // 6. Glossary
    this.pdf.addSectionHeading(annT.glossaryTitle, 2);
    this.pdf.addParagraph(annT.glossaryDesc);
    this.pdf.addSpace(2);

    const glossary = annT.glossaryTable;
    this.pdf.addTable(glossary[0], glossary.slice(1), {
      columnWidths: [50, 120],
      alternateRows: true,
    });
    this.pdf.addSpace(5);

    // 7. Risk matrix schema
    this.pdf.addSectionHeading(annT.matrixSchemaTitle, 2);
    this.pdf.addParagraph(annT.matrixSchemaDesc);
    this.pdf.addSpace(3);

    const doc = this.pdf.pdf;
    const startX = this.pdf.margin + 20;
    const startY = this.pdf.currentY;
    const cellSize = 25;

    const matrixLevels = this.reportData.riskLevels || [];
    const matrixCount = this.reportData.riskLevelsCount || 5;
    const matrixColors = {
      green: [34, 197, 94],
      yellow: [234, 179, 8],
      orange: [249, 115, 22],
      red: [220, 38, 38],
    };

    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(
      this.T.annexes.matrixGravityAxis,
      startX - 10,
      startY + cellSize * 2.5,
      { angle: 90, align: "center" },
    );
    doc.text(
      this.T.annexes.matrixLikelihoodAxis,
      startX + cellSize * 2,
      startY + cellSize * (matrixCount + 1) + 10,
      { align: "center" },
    );

    const getCellColor = (gravity, likelihood) => {
      if (matrixLevels.length > 0) {
        const score = gravity * likelihood;
        const lv = matrixLevels.find(
          (l) => score >= l.minScore && score <= l.maxScore
        );
        return matrixColors[lv?.color] || matrixColors.green;
      }
      const hardcodedGrid = [
        ["red", "red", "red", "red"],
        ["orange", "red", "red", "red"],
        ["yellow", "orange", "red", "red"],
        ["green", "yellow", "orange", "red"],
        ["green", "green", "yellow", "orange"],
      ];
      const n = hardcodedGrid.length;
      const rowIdx = n - gravity;
      if (rowIdx >= 0 && rowIdx < n) {
        return matrixColors[hardcodedGrid[rowIdx][likelihood - 1]];
      }
      return matrixColors.green;
    };

    for (let r = 0; r < matrixCount; r++) {
      const y = startY + r * cellSize;
      const gravity = matrixCount - r;

      let gravityLabel;
      if (matrixLevels.length > 0) {
        const lv = matrixLevels.find((l) => l.level === gravity);
        gravityLabel = lv?.label || `Niv. ${gravity}`;
      } else {
        gravityLabel = `G${gravity}`;
      }

      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(gravityLabel, startX - 5, y + cellSize / 2 + 2, {
        align: "right",
      });

      for (let c = 0; c < 4; c++) {
        const x = startX + c * cellSize;
        const likelihood = c + 1;
        doc.setFillColor(...getCellColor(gravity, likelihood));
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(0.5);
        doc.rect(x, y, cellSize, cellSize, "FD");
      }
    }

    for (let l = 1; l <= 4; l++) {
      const x = startX + (l - 1) * cellSize;
      const y = startY + matrixCount * cellSize;
      doc.setTextColor(0, 0, 0);
      doc.text(`V${l}`, x + cellSize / 2, y + 5, { align: "center" });
    }

    const legendX = startX + cellSize * 4 + 10;
    let legendY = startY;

    if (matrixLevels.length > 0) {
      matrixLevels.forEach((lv) => {
        doc.setFillColor(...(matrixColors[lv.color] || matrixColors.green));
        doc.rect(legendX, legendY, 5, 5, "F");
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        doc.text(lv.label, legendX + 8, legendY + 4);
        legendY += 10;
      });
    } else {
      const fallbackLegend = [
        { label: this.language === 'en' ? 'Critical Risk' : 'Risque Critique', color: "red" },
        { label: this.language === 'en' ? 'Major Risk' : 'Risque Majeur', color: "orange" },
        { label: this.language === 'en' ? 'Significant Risk' : 'Risque Significatif', color: "yellow" },
        { label: this.language === 'en' ? 'Minor Risk' : 'Risque Mineur', color: "green" },
      ];
      fallbackLegend.forEach((item) => {
        doc.setFillColor(...(matrixColors[item.color]));
        doc.rect(legendX, legendY, 5, 5, "F");
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        doc.text(item.label, legendX + 8, legendY + 4);
        legendY += 10;
      });
    }

    this.pdf.currentY += cellSize * matrixCount + 30;

    // Note about preliminary-study markers
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.setFont("helvetica", "italic");
    doc.text(
      this.language === 'en'
        ? "* = likelihood from a preliminary study (Workshop 3, no operational scenario)."
        : "* = vraisemblance issue d'une \u00e9tude pr\u00e9liminaire (Atelier 3, pas de sc\u00e9nario op\u00e9rationnel).",
      startX,
      startY + cellSize * matrixCount + 18,
    );
  }
}

export default MatrixReportGenerator;

